import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Check, X, Star, Flag, Mail, Plus, Trash2, Shield, Download, MapPin, Copy, ExternalLink, Users, Phone, Sparkles, Pencil, Inbox, Trophy, BellRing, AlertTriangle, Target, TrendingUp, Zap, Facebook, BarChart3, Clock, Share2, Repeat } from "lucide-react";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { PostcardSettings } from "@/components/PostcardSettings";
import CareersAdmin, { CareersNeedsBadge } from "@/components/CareersAdmin";
import ConsentLog from "@/components/ConsentLog";
import RenewalsPanel from "@/components/RenewalsPanel";
import TerritoryMap from "@/components/TerritoryMap";
import LeadsBoard from "@/components/LeadsBoard";
import PartnerAdmin from "@/components/PartnerAdmin";
import RoutesAdmin, { PostageSettings } from "@/components/RoutesAdmin";
import ProspectingAdmin, { ProspectorTiers } from "@/components/ProspectingAdmin";
import PodcastAdmin from "@/components/PodcastAdmin";
import AffiliatesAdmin from "@/components/AffiliatesAdmin";
import OnboardingAdmin from "@/components/OnboardingAdmin";

const STAGE_COLOR = { new: "bg-sam-slate", contacted: "bg-sam-amber", sold: "bg-sam-green", declined: "bg-slate-400" };
const LEAD_SOURCE_LABEL = {
  hvac: "HVAC", plumber: "Plumbing", roofer: "Roofing", electrician: "Electrical",
  landscaper: "Landscaping", business: "Business", service: "Service",
  "realestate-buy": "Buy a home", "realestate-sell": "Sell a home",
};
const OUTREACH_STAGES = ["new", "sent_to_outvid", "video_watched", "call_booked", "won", "lost"];
const LOST_REASONS = ["No budget", "Went with competitor", "No response", "Not a fit", "Bad timing", "Other"];
const LOST_TIPS = {
  "No budget": "Offer the smaller Basic ad size or a split-pay option so the cost fits.",
  "Went with competitor": "Stress the co-op shared cost and the exclusive one-per-trade spot they can't get elsewhere.",
  "No response": "Send a 15-second personal video and try a different time of day.",
  "Not a fit": "Ask which trades/areas they serve and match them to a better ZIP route.",
  "Bad timing": "Set a reminder to re-approach right before their busy season starts.",
  "Other": "Log the specific reason in notes so you can tailor the next pitch.",
  "Unspecified": "Capture the real reason next time to sharpen your pitch.",
};
const OUTREACH_LABEL = { new: "New", sent_to_outvid: "Sent to Outvid", video_watched: "Video Watched", call_booked: "Call Booked", won: "Won", lost: "Lost" };
const OUTREACH_COLOR = { new: "bg-sam-slate", sent_to_outvid: "bg-sam-amber", video_watched: "bg-sam-navy", call_booked: "bg-blue-500", won: "bg-sam-green", lost: "bg-slate-400" };
const BOOKING_LINK = "https://cal.com/saving.america.money/schedule-a-discovery-call";

export default function Admin() {
  const { user } = useAuth();
  const can = (a) => user?.role === "admin" || (user?.perms || []).includes(a);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [runs, setRuns] = useState([]);
  const [states, setStates] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runForm, setRunForm] = useState({ route_code: "", city: "", central_zip: "", state_abbr: "MO", metro: "", mailer_count: 10000, assigned_rep: "" });
  const [refCode, setRefCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [subaccounts, setSubaccounts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState("all");
  const [videoQueue, setVideoQueue] = useState([]);
  const [vqStageFilter, setVqStageFilter] = useState(null);
  const [paidRevenue, setPaidRevenue] = useState(0);
  const [wonRevenue, setWonRevenue] = useState(0);
  const [wonDialog, setWonDialog] = useState({ open: false, id: null, amount: "", edit: false });
  const [monthRevenue, setMonthRevenue] = useState(0);
  const [monthlyGoal, setMonthlyGoal] = useState(0);
  const [pipelineCount, setPipelineCount] = useState(0);
  const [pipelineValue, setPipelineValue] = useState(0);
  const [goalEdit, setGoalEdit] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [vqUnsent, setVqUnsent] = useState(true);
  const [vqBusy, setVqBusy] = useState(false);
  const [refStageFilter, setRefStageFilter] = useState("all");
  const [tab, setTab] = useState("approvals");
  const [lostDialog, setLostDialog] = useState({ open: false, id: null });
  const [pipeOpen, setPipeOpen] = useState(false);
  const [revHistory, setRevHistory] = useState([]);
  const [smsUnread, setSmsUnread] = useState(0);
  const [planFailed, setPlanFailed] = useState(0);
  const refreshUnread = useCallback(() => { api.get("/admin/sms/unread").then(({ data }) => setSmsUnread(data.unread || 0)).catch(() => {}); }, []);
  useEffect(() => { refreshUnread(); const t = setInterval(refreshUnread, 60000); return () => clearInterval(t); }, [refreshUnread]);
  useEffect(() => { api.get("/my/affiliate").then(({ data }) => setRefCode(data.code || "")).catch(() => {}); }, []);

  const load = useCallback(async () => {
    const [s, p, f, r, st, w, ref, sub, ld, vq, rh] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/pending-businesses"),
      api.get("/admin/flagged-reviews"), api.get("/postcard-runs"), api.get("/states"),
      api.get("/admin/waitlist"), api.get("/admin/referrals"),
      api.get("/admin/subaccounts"), api.get("/admin/leads"), api.get("/admin/video-queue"), api.get("/admin/revenue-history"),
    ]);
    setStats(s.data); setPending(p.data); setFlagged(f.data); setRuns(r.data); setStates(st.data); setWaitlist(w.data); setReferrals(ref.data); setSubaccounts(sub.data); setLeads(ld.data); setVideoQueue(vq.data.leads || []); setPaidRevenue(vq.data.paid_revenue || 0); setWonRevenue(vq.data.won_revenue || 0); setMonthRevenue(vq.data.month_revenue || 0); setMonthlyGoal(vq.data.monthly_goal || 0); setPipelineCount(vq.data.pipeline_count || 0); setPipelineValue(vq.data.pipeline_value || 0); setRevHistory(rh.data.history || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resendInvite = async (bid) => {
    try {
      const { data } = await api.post(`/my/businesses/${bid}/resend-invite`);
      if (data.status === "already_pending") toast.info(`Invite already pending for ${data.email} — ask them to check spam.`);
      else if (data.ok) toast.success(`Invite re-sent to ${data.email}`);
      else toast.error("Couldn't resend — invite may already be accepted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend invite"); }
  };

  const approve = async (id) => { await api.post(`/admin/businesses/${id}/approve`); toast.success("Approved"); load(); };
  const reject = async (id) => { await api.post(`/admin/businesses/${id}/reject`); toast("Rejected"); load(); };
  const celebrateWon = (addedAmount = 0, goalCrossed = false) => {
    try {
      confetti({ particleCount: 120, spread: 75, origin: { y: 0.6 } });
      setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 60, origin: { x: 0 } }), 150);
      setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1 } }), 150);
      if (goalCrossed) setTimeout(() => confetti({ particleCount: 220, spread: 120, startVelocity: 55, origin: { y: 0.5 } }), 300);
    } catch (e) { /* confetti is best-effort */ }
    const wonCount = videoQueue.filter((l) => l.outreach_stage === "won").length + 1;
    const total = Number(wonRevenue || 0) + Number(addedAmount || 0);
    const rev = total ? ` · $${total.toLocaleString()} booked` : "";
    if (goalCrossed) toast.success(`Monthly goal smashed! ${wonCount} closed${rev}`, { icon: <Target className="h-4 w-4 text-sam-crimson" />, duration: 6000 });
    else toast.success(`Deal won! ${wonCount} closed${rev}`, { icon: <Trophy className="h-4 w-4 text-sam-green" />, duration: 5000 });
  };
  const setOutreachStage = async (id, stage, wonAmount, lostReason) => {
    const prev = videoQueue.find((l) => l.id === id)?.outreach_stage || "new";
    const nowIso = new Date().toISOString();
    setVideoQueue((p) => p.map((l) => (l.id === id ? { ...l, outreach_stage: stage, stage_changed_at: nowIso, ...(stage === "won" ? { won_at: nowIso, won_amount: Number(wonAmount) || l.won_amount || 0 } : {}), ...(stage === "lost" ? { lost_reason: lostReason || l.lost_reason || "" } : {}) } : l)));
    try {
      await api.patch(`/admin/leads/${id}/outreach-stage`, { stage, ...(wonAmount != null ? { won_amount: Number(wonAmount) } : {}), ...(lostReason ? { lost_reason: lostReason } : {}) });
      if (stage === "won" && prev !== "won") {
        const add = Number(wonAmount) || 0;
        const goalCrossed = Number(monthlyGoal) > 0 && Number(monthRevenue) < Number(monthlyGoal) && Number(monthRevenue) + add >= Number(monthlyGoal);
        celebrateWon(add, goalCrossed);
        setWonRevenue((val) => Number(val || 0) + add);
        setMonthRevenue((val) => Number(val || 0) + add);
      } else toast.success(`Marked ${stage.replace(/_/g, " ")}`);
    } catch (e) { toast.error("Could not update stage"); load(); }
  };
  const confirmWon = async () => {
    const amt = parseFloat(wonDialog.amount);
    const finalAmt = isNaN(amt) ? 0 : amt;
    const isEdit = !!wonDialog.edit;
    const id = wonDialog.id;
    setWonDialog({ open: false, id: null, amount: "", edit: false });
    await setOutreachStage(id, "won", finalAmt);
    if (isEdit) load();
  };
  const confirmLost = async (reason) => {
    const id = lostDialog.id;
    setLostDialog({ open: false, id: null });
    await setOutreachStage(id, "lost", null, reason);
  };
  const saveGoal = async () => {
    const g = parseFloat(goalInput);
    if (isNaN(g) || g < 0) { toast.error("Enter a valid goal"); return; }
    setMonthlyGoal(g); setGoalEdit(false);
    try { await api.patch("/admin/outreach-settings", { monthly_revenue_goal: g }); toast.success("Monthly goal updated"); }
    catch (e) { toast.error("Could not save goal"); }
  };
  const sendReminder = async (id) => {
    try { const { data } = await api.post(`/admin/leads/${id}/remind`); const ch = [data.emailed && "email", data.texted && "text"].filter(Boolean).join(" + "); toast.success(ch ? `Reminder sent (${ch})` : "Reminder logged"); load(); }
    catch (e) { toast.error("Could not send reminder"); }
  };
  const markSentOutvid = async (id) => {
    try { await api.post(`/admin/video-queue/mark-sent`, { ids: [id] }); toast.success("Marked sent to Outvid"); load(); }
    catch (e) { toast.error("Could not mark sent"); }
  };
  const exportOutvidCsv = async () => {
    setVqBusy(true);
    try {
      const res = await api.get("/admin/video-queue/export?only_unsent=true", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "outvid_prospects.csv"; a.click();
      window.URL.revokeObjectURL(url);
      const skipped = Number(res.headers?.["x-skipped-count"] || 0);
      toast.success(skipped > 0 ? `CSV exported — ${skipped} duplicate-trade row(s) auto-skipped` : "CSV exported — leads marked Sent to Outvid");
      load();
    } catch (e) { toast.error("Export failed"); }
    finally { setVqBusy(false); }
  };
  const setReferralStage = async (id, stage) => {
    setReferrals((prev) => prev.map((r) => (r.id === id ? { ...r, stage } : r)));
    try { await api.patch(`/admin/referrals/${id}/stage`, { stage }); toast.success(`Marked ${stage}`); }
    catch (e) { toast.error("Could not update status"); load(); }
  };
  const setLeadStage = async (id, stage) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, stage } : l)));
    try { await api.patch(`/admin/leads/${id}/stage`, { stage }); toast.success(`Marked ${stage}`); }
    catch (e) { toast.error("Could not update status"); load(); }
  };
  const createRun = async () => {
    if (!runForm.route_code || !runForm.city) return toast.error("Route code and city required");
    const { data } = await api.post("/postcard-runs", runForm);
    toast.success(runForm.central_zip ? "Run created + ZIP page auto-generated" : "Postcard run created with 12 slots");
    if (data.assigned_rep) toast(`Rep ${data.assigned_rep} assigned (${data.assigned_rep_source === "zip_territory" ? "area ZIP" : data.assigned_rep_source})`);
    else toast.warning("No rep for this ZIP — assign one under Postcard Settings → Reps per run");
    load();
    setRunForm({ route_code: "", city: "", central_zip: "", state_abbr: "MO", metro: "", mailer_count: 10000, assigned_rep: "" });
  };
  const releaseSlot = async (sid) => { await api.post(`/postcard-slots/${sid}/release`); toast("Slot released"); load(); };
  const copyUrl = (u) => { navigator.clipboard?.writeText(window.location.origin + u); toast.success("URL copied"); };

  if (loading) return <div className="h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;

  const STAT = [
    { label: "Businesses", value: stats.businesses }, { label: "Pending", value: stats.pending },
    { label: "Users", value: stats.users }, { label: "Reviews", value: stats.reviews },
    { label: "Postcard runs", value: stats.runs }, { label: "Flagged", value: stats.flagged },
    { label: "Referrals", value: stats.referrals ?? 0, onClick: () => setTab("referrals"), testid: "stat-referrals" },
    { label: "Leads", value: leads.length, onClick: () => setTab("leads"), testid: "stat-leads" },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl font-extrabold text-sam-navy flex items-center gap-2 mb-6"><Shield className="h-7 w-7 text-sam-crimson" />Admin Control</h1>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
        {STAT.map((s) => (
          <div key={s.label} data-testid={s.testid} onClick={s.onClick} role={s.onClick ? "button" : undefined}
            className={`rounded-xl border border-border bg-card p-4 text-center ${s.onClick ? "cursor-pointer hover:bg-sam-crimson/10 hover:border-sam-crimson/30 transition-colors" : ""}`}>
            <div className="font-mono text-2xl font-bold text-sam-navy">{s.value}</div>
            <div className="text-xs text-sam-slate">{s.label}{s.onClick ? " ›" : ""}</div>
          </div>
        ))}
      </div>

      {user?.role === "staff" && (() => {
        const L = { leads: "Leads & Directory", sms: "SMS Inbox", plans: "Plans & Sub-accounts", credits: "Credit Budgets", seo: "SEO & Blog", outreach: "Outreach" };
        const ps = user.perms || [];
        return (
          <div className="mb-4 rounded-xl border border-sam-navy/15 bg-sam-navy/5 px-4 py-3" data-testid="staff-access-banner">
            <p className="text-sm text-sam-navy"><span className="font-semibold">You're signed in as team staff.</span> You can access: {ps.length ? ps.map((p) => L[p] || p).join(" · ") : "no areas yet — ask your admin to grant access."}</p>
          </div>
        );
      })()}
      {user?.referly_code && (
        <div className="mb-4 rounded-xl border border-sam-crimson/25 bg-sam-crimson/5 px-4 py-3" data-testid="my-affiliate-card">
          <p className="text-sm font-semibold text-sam-navy mb-1">Your affiliate referral link</p>
          <div className="flex flex-wrap items-center gap-2">
            <input readOnly value={`${window.location.origin}/?ref=${user.referly_code}`} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 text-xs font-mono bg-white border border-border rounded px-2 py-1.5" data-testid="my-affiliate-link" />
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/?ref=${user.referly_code}`); toast.success("Link copied"); }} data-testid="my-affiliate-copy">Copy link</Button>
          </div>
          <p className="text-[11px] text-sam-slate mt-1">Share this link — any plan, credit pack or postcard purchase that starts from it is credited to you in Referly.</p>
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full max-w-full justify-start overflow-x-auto">
          {can("leads") && <TabsTrigger value="approvals" data-testid="tab-approvals">Approvals {pending.length > 0 && <Badge className="ml-1.5 bg-sam-crimson">{pending.length}</Badge>}</TabsTrigger>}
          {can("leads") && <TabsTrigger value="postcards" data-testid="tab-postcards">Postcard Runs</TabsTrigger>}
          {can("leads") && <TabsTrigger value="flagged" data-testid="tab-flagged">Flagged {flagged.length > 0 && <Badge className="ml-1.5 bg-sam-amber">{flagged.length}</Badge>}</TabsTrigger>}
          {can("leads") && <TabsTrigger value="demand" data-testid="tab-demand">Demand {waitlist.length > 0 && <Badge className="ml-1.5 bg-sam-navy">{waitlist.length}</Badge>}</TabsTrigger>}
          {can("leads") && <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals {referrals.length > 0 && <Badge className="ml-1.5 bg-sam-green">{referrals.length}</Badge>}</TabsTrigger>}
          {can("leads") && <TabsTrigger value="leads" data-testid="tab-leads">Leads {leads.length > 0 && <Badge className="ml-1.5 bg-sam-crimson">{leads.length}</Badge>}</TabsTrigger>}
          {can("outreach") && <TabsTrigger value="video" data-testid="tab-video">Video Outreach {videoQueue.filter((v) => !v.outvid_sent).length > 0 && <Badge className="ml-1.5 bg-sam-amber">{videoQueue.filter((v) => !v.outvid_sent).length}</Badge>}</TabsTrigger>}
          {can("plans") && <TabsTrigger value="crm" data-testid="tab-crm">CRM {subaccounts.length > 0 && <Badge className="ml-1.5 bg-sam-green">{subaccounts.length}</Badge>}</TabsTrigger>}
          {can("sms") && <TabsTrigger value="inbox" data-testid="tab-inbox">SMS Inbox {smsUnread > 0 && <Badge className="ml-1.5 bg-sam-crimson" data-testid="tab-inbox-unread">{smsUnread}</Badge>}</TabsTrigger>}
          {can("seo") && <TabsTrigger value="seo" data-testid="tab-seo">SEO / Blog</TabsTrigger>}
          {(can("plans") || can("credits")) && <TabsTrigger value="plans" data-testid="tab-plans">Plans & Sub-accounts {planFailed > 0 && <Badge className="ml-1.5 bg-sam-crimson" data-testid="tab-plans-failed">{planFailed}</Badge>}</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="pc-settings" data-testid="tab-pc-settings">Postcard Settings</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="podcast" data-testid="tab-podcast">Podcast</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="routes" data-testid="tab-routes">Routes</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="prospecting" data-testid="tab-prospecting">Prospecting</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="careers" data-testid="tab-careers">Careers<CareersNeedsBadge /></TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="consent" data-testid="tab-consent">Consent Log</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="renewals" data-testid="tab-renewals">Renewals</TabsTrigger>}
        </TabsList>
        {user?.role === "admin" && (
          <>
            <TabsContent value="pc-settings" className="pt-4">
              <PostageSettings />
              <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-border bg-slate-50/60 p-3 flex-wrap" data-testid="dues-health-check">
                <div className="text-sm text-sam-slate"><b className="text-sam-navy">Enforcement health check.</b> Send yourself a test alert to confirm postcard-dues failure emails reach your inbox.</div>
                <Button size="sm" variant="outline" data-testid="dues-self-test-btn" onClick={async () => { try { const { data } = await api.post("/admin/cron/self-test"); toast.success(`Test alert sent to ${data.sent_to || "your inbox"}`); } catch { toast.error("Could not send test alert"); } }}><BellRing className="h-4 w-4 mr-1" />Send dues alert test</Button>
              </div>
              <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-border bg-slate-50/60 p-3 flex-wrap" data-testid="sample-receipt-check">
                <div className="text-sm text-sam-slate"><b className="text-sam-navy">Buyer receipt preview.</b> Email yourself a real sample purchase receipt (PDF + timestamped Terms consent line) exactly as advertisers receive it.</div>
                <Button size="sm" variant="outline" data-testid="sample-receipt-btn" onClick={async () => { try { const { data } = await api.post("/admin/email/sample-receipt"); toast.success(`Sample receipt sent to ${data.sent_to || "your inbox"}`); } catch (e) { toast.error(e?.response?.data?.detail || "Could not send sample receipt"); } }}><Mail className="h-4 w-4 mr-1" />Send sample receipt</Button>
              </div>
              <PostcardSettings />
            </TabsContent>
            <TabsContent value="podcast" className="pt-4"><PodcastAdmin /></TabsContent>
            <TabsContent value="routes" className="pt-4"><RoutesAdmin /></TabsContent>
            <TabsContent value="prospecting" className="pt-4"><ProspectingAdmin /></TabsContent>
            <TabsContent value="team" className="pt-4"><OnboardingAdmin /><AffiliatesAdmin /><ProspectorTiers /><PartnerAdmin /><TerritoryMap /><TeamPanel /></TabsContent>
            <TabsContent value="careers" className="pt-4"><CareersAdmin /></TabsContent>
            <TabsContent value="consent" className="pt-4"><ConsentLog /></TabsContent>
            <TabsContent value="renewals" className="pt-4"><RenewalsPanel /></TabsContent>
          </>
        )}

        <TabsContent value="approvals" className="pt-4">
          {pending.length === 0 ? <p className="text-sam-slate text-sm">No listings waiting for approval. 🎉</p> : (
            <div className="space-y-3">
              {pending.map((b) => (
                <div key={b.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3" data-testid={`pending-${b.slug}`}>
                  <div>
                    <div className="font-display font-bold text-sam-navy">{b.name}</div>
                    <div className="text-sm text-sam-slate">{b.category} · {b.city}, {b.state_abbr}</div>
                    <p className="text-xs text-sam-slate mt-1 max-w-lg line-clamp-2">{b.short_description}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" className="bg-sam-green hover:bg-sam-green/90" onClick={() => approve(b.id)} data-testid={`approve-${b.slug}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(b.id)} data-testid={`reject-${b.slug}`}><X className="h-4 w-4 mr-1" />Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="postcards" className="pt-4 space-y-6">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-display font-bold text-sam-navy mb-3 flex items-center gap-2"><Plus className="h-4 w-4" />New postcard run</h3>
            <div className="grid sm:grid-cols-6 gap-3">
              <div><Label className="text-xs">Route code</Label><Input value={runForm.route_code} onChange={(e) => setRunForm({ ...runForm, route_code: e.target.value })} placeholder="MO-SGF-02" data-testid="run-code" /></div>
              <div><Label className="text-xs">City</Label><Input value={runForm.city} onChange={(e) => setRunForm({ ...runForm, city: e.target.value })} data-testid="run-city" /></div>
              <div><Label className="text-xs">Central ZIP</Label><Input value={runForm.central_zip} onChange={(e) => setRunForm({ ...runForm, central_zip: e.target.value })} placeholder="65708" data-testid="run-zip" /></div>
              <div><Label className="text-xs">Rep code <span className="font-normal text-sam-slate">(blank = auto)</span></Label><Input value={runForm.assigned_rep || ""} onChange={(e) => setRunForm({ ...runForm, assigned_rep: e.target.value })} placeholder="auto by ZIP" data-testid="run-rep" /></div>
              <div><Label className="text-xs">State</Label>
                <Select value={runForm.state_abbr} onValueChange={(v) => setRunForm({ ...runForm, state_abbr: v })}>
                  <SelectTrigger data-testid="run-state"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{states.map((s) => <SelectItem key={s.abbr} value={s.abbr}>{s.abbr}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Metro</Label><Input value={runForm.metro} onChange={(e) => setRunForm({ ...runForm, metro: e.target.value })} /></div>
              <div className="flex items-end"><Button className="w-full bg-sam-navy" onClick={createRun} data-testid="create-run-btn">Create</Button></div>
            </div>
            <p className="text-xs text-sam-slate mt-2">Add a Central ZIP to auto-generate a shareable <span className="font-mono">/us/state/city/zip</span> mailer page. Edit its headline/note under <b>Manage slots</b> below.</p>
          </div>
          <div className="space-y-3">
            {[...runs].sort((a, b) => {
              const fill = (x) => { const t = (x.sold_count || 0) + (x.available_count || 0); return t ? x.sold_count / t : 0; };
              const done = (x) => (x.available_count === 0 ? 1 : 0);
              if (done(a) !== done(b)) return done(a) - done(b);
              return fill(b) - fill(a);
            }).map((r) => <RunRow key={r.id} run={r} refCode={refCode} />)}
          </div>
          <ShareLog />
        </TabsContent>

        <TabsContent value="flagged" className="pt-4">
          {flagged.length === 0 ? <p className="text-sam-slate text-sm">No flagged reviews.</p> : (
            <div className="space-y-3">
              {flagged.map((r) => (
                <div key={r.id} className="rounded-xl border border-sam-amber/40 bg-sam-amber/5 p-4">
                  <div className="flex items-center gap-2 text-sm"><Flag className="h-4 w-4 text-sam-amber" /><span className="font-semibold">{r.user_name}</span><span className="flex items-center gap-0.5 text-sam-amber"><Star className="h-3 w-3 fill-current" />{r.rating}</span></div>
                  <p className="text-sm text-sam-slate mt-1">{r.text}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="demand" className="pt-4">
          {waitlist.length === 0 ? <p className="text-sam-slate text-sm">No waitlist sign-ups yet. Shoppers on empty city pages can ask to be notified.</p> : (
            <div className="space-y-2" data-testid="admin-waitlist">
              <p className="text-sm text-sam-slate mb-2">Markets shoppers are asking for — highest demand first.</p>
              {waitlist.map((w) => (
                <div key={`${w.state_abbr}-${w.city_slug}`} data-testid={`waitlist-row-${w.city_slug}`}
                  className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
                  <Link to={`/city/${w.city_slug}?state=${w.state_abbr}`} className="flex items-center gap-2 font-display font-bold text-sam-navy hover:text-sam-crimson transition-colors">
                    <MapPin className="h-4 w-4 text-sam-crimson" />{w.city_name}, {w.state_abbr}
                  </Link>
                  <Badge className="bg-sam-navy">{w.count} {w.count === 1 ? "person" : "people"}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="referrals" className="pt-4 space-y-4">
          <ReferralLeaderboard />
          {referrals.length === 0 ? <p className="text-sam-slate text-sm">No referrals yet. Premium advertisers are emailed a link to refer trusted businesses for the other 11 spaces.</p> : (
            <div className="space-y-2" data-testid="admin-referrals">
              <p className="text-sm text-sam-slate mb-2">Businesses referred by your Premium advertisers — newest first. Also synced to Zuops CRM.</p>
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="referral-stage-filter">
                {[["all", "All"], ["new", "New"], ["contacted", "Contacted"], ["sold", "Sold"], ["declined", "Declined"]].map(([key, label]) => {
                  const count = key === "all" ? referrals.length : referrals.filter((r) => (r.stage || "new") === key).length;
                  const active = refStageFilter === key;
                  return (
                    <button key={key} onClick={() => setRefStageFilter(key)} data-testid={`ref-filter-${key}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${active ? "bg-sam-navy text-white" : "bg-sam-navy/5 text-sam-navy hover:bg-sam-navy/10"}`}>
                      {label}<span className={`rounded-full px-1.5 ${active ? "bg-white/20" : "bg-sam-navy/10"}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
              {(() => {
                const shown = refStageFilter === "all" ? referrals : referrals.filter((r) => (r.stage || "new") === refStageFilter);
                if (shown.length === 0) return <p className="text-sm text-sam-slate py-4" data-testid="referral-empty-filter">No referrals in "{refStageFilter}".</p>;
                return shown.map((r) => (
                <div key={r.id} data-testid={`referral-row-${r.id}`}
                  className="rounded-xl border border-border bg-card p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 font-display font-bold text-sam-navy">
                        <Users className="h-4 w-4 text-sam-crimson" />{r.referred_name}
                      </div>
                      <div className="text-sm text-sam-slate mt-0.5">
                        {r.contact_name && <span className="mr-2">{r.contact_name}</span>}
                        {r.contact_email && <a href={`mailto:${r.contact_email}`} className="mr-2 hover:text-sam-crimson"><Mail className="h-3 w-3 inline mr-1" />{r.contact_email}</a>}
                        {r.contact_phone && <a href={`tel:${r.contact_phone}`} className="hover:text-sam-crimson"><Phone className="h-3 w-3 inline mr-1" />{r.contact_phone}</a>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {r.ad_size && <Badge variant="outline" className="capitalize">{r.ad_size}</Badge>}
                      {(r.city || r.state_abbr || r.central_zip) && <Badge className="bg-sam-navy">{[r.city, r.state_abbr].filter(Boolean).join(", ")} {r.central_zip}</Badge>}
                      <Select value={r.stage || "new"} onValueChange={(v) => setReferralStage(r.id, v)}>
                        <SelectTrigger className={`h-7 w-[120px] text-xs font-semibold border-0 text-white ${STAGE_COLOR[r.stage || "new"]}`} data-testid={`referral-stage-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="sold">Sold</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {r.referrer && <p className="text-xs text-sam-slate mt-2">Referred by <span className="font-semibold text-sam-navy">{r.referrer}</span></p>}
                  {r.notes && <p className="text-sm text-sam-navy/80 mt-1 italic">"{r.notes}"</p>}
                  {r.central_zip && (
                    <Link to={`/mailer?zip=${r.central_zip}&city=${encodeURIComponent(r.city || "")}&state=${r.state_abbr || ""}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-sam-crimson mt-2 hover:underline" data-testid={`referral-sell-${r.id}`}>
                      Sell them a spot <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
                ));
              })()}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leads" className="pt-4">
          <LeadsBoard />
          {leads.length === 0 ? <p className="text-sam-slate text-sm">No landing-page leads yet. Submissions from your HVAC, Plumber, Roofer, Electrician, Landscaper, Grow-my-business, and Buy/Sell-a-home pages appear here (and sync to Zuops).</p> : (
            <div className="space-y-2" data-testid="admin-leads">
              <p className="text-sm text-sam-slate mb-2">Leads captured from your marketing landing pages — newest first. Also synced to Zuops CRM.</p>
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="lead-source-filter">
                {(() => {
                  const sources = Array.from(new Set(leads.map((l) => l.source)));
                  const chips = [["all", "All"], ...sources.map((s) => [s, LEAD_SOURCE_LABEL[s] || s])];
                  return chips.map(([key, label]) => {
                    const count = key === "all" ? leads.length : leads.filter((l) => l.source === key).length;
                    const active = leadFilter === key;
                    return (
                      <button key={key} onClick={() => setLeadFilter(key)} data-testid={`lead-filter-${key}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${active ? "bg-sam-navy text-white" : "bg-sam-navy/5 text-sam-navy hover:bg-sam-navy/10"}`}>
                        {label}<span className={`rounded-full px-1.5 ${active ? "bg-white/20" : "bg-sam-navy/10"}`}>{count}</span>
                      </button>
                    );
                  });
                })()}
              </div>
              {(() => {
                const shown = leadFilter === "all" ? leads : leads.filter((l) => l.source === leadFilter);
                if (shown.length === 0) return <p className="text-sm text-sam-slate py-4" data-testid="lead-empty-filter">No leads for this filter.</p>;
                return shown.map((l) => (
                  <div key={l.id} data-testid={`lead-row-${l.id}`} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-display font-bold text-sam-navy">
                          <Inbox className="h-4 w-4 text-sam-crimson" />{l.name}
                          {l.company && <span className="text-sm font-normal text-sam-slate">· {l.company}</span>}
                        </div>
                        <div className="text-sm text-sam-slate mt-0.5">
                          {l.email && <a href={`mailto:${l.email}`} className="mr-2 hover:text-sam-crimson"><Mail className="h-3 w-3 inline mr-1" />{l.email}</a>}
                          {l.phone && <a href={`tel:${l.phone}`} className="hover:text-sam-crimson"><Phone className="h-3 w-3 inline mr-1" />{l.phone}</a>}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline">{LEAD_SOURCE_LABEL[l.source] || l.source}</Badge>
                        {(l.city || l.state_abbr || l.zip) && <Badge className="bg-sam-navy">{[l.city, l.state_abbr].filter(Boolean).join(", ")} {l.zip}</Badge>}
                        <Select value={l.stage || "new"} onValueChange={(v) => setLeadStage(l.id, v)}>
                          <SelectTrigger className={`h-7 w-[120px] text-xs font-semibold border-0 text-white ${STAGE_COLOR[l.stage || "new"]}`} data-testid={`lead-stage-${l.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="sold">Sold</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {l.timeframe && <Badge variant="outline" className="text-xs">⏱ {l.timeframe}</Badge>}
                      {l.budget && <Badge variant="outline" className="text-xs">💲 {l.budget}</Badge>}
                      {(l.needs || []).map((n) => <Badge key={n} variant="outline" className="text-xs">{n}</Badge>)}
                    </div>
                    {l.notes && <p className="text-sm text-sam-navy/80 mt-2 italic">"{l.notes}"</p>}
                  </div>
                ));
              })()}
            </div>
          )}
        </TabsContent>

        <TabsContent value="video" className="pt-4">
          <ReferralLedger />
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-sm text-sam-slate">Personalized video outreach via Outvid. Export the queue, import into Outvid, and track each lead through to a booked call.</p>
              <a href={BOOKING_LINK} target="_blank" rel="noreferrer" className="text-xs font-semibold text-sam-crimson hover:underline" data-testid="vq-booking-link">Discovery call link: {BOOKING_LINK}</a>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setVqUnsent((v) => !v)} data-testid="vq-toggle" className="text-xs font-semibold text-sam-navy bg-sam-navy/5 hover:bg-sam-navy/10 rounded-full px-3 py-1.5">{vqUnsent ? "Showing: Not sent" : "Showing: All"}</button>
              <Button size="sm" onClick={exportOutvidCsv} disabled={vqBusy} className="bg-sam-crimson hover:bg-sam-crimson/90" data-testid="vq-export-btn">
                {vqBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" />Export CSV for Outvid</>}
              </Button>
            </div>
          </div>
          {(() => {
            const rank = { new: 0, sent_to_outvid: 1, video_watched: 2, call_booked: 3, won: 4, lost: -1 };
            const r = (v) => rank[v.outreach_stage || "new"] ?? 0;
            const total = videoQueue.length;
            const sent = videoQueue.filter((v) => r(v) >= 1).length;
            const watched = videoQueue.filter((v) => r(v) >= 2).length;
            const booked = videoQueue.filter((v) => r(v) >= 3).length;
            const won = videoQueue.filter((v) => r(v) >= 4).length;
            const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
            const strip = [
              { key: "sent", label: "Sent", count: sent, color: "text-sam-amber", ring: "ring-sam-amber", rate: `${pct(sent, total)}% of leads` },
              { key: "watched", label: "Watched", count: watched, color: "text-sam-navy", ring: "ring-sam-navy", rate: `${pct(watched, sent)}% of sent` },
              { key: "booked", label: "Call Booked", count: booked, color: "text-blue-500", ring: "ring-blue-500", rate: `${pct(booked, sent)}% of sent` },
              { key: "won", label: "Won", count: won, color: "text-sam-green", ring: "ring-sam-green", rate: `${pct(won, sent)}% of sent` },
            ];
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="vq-stats-strip">
                {strip.map((s) => {
                  const active = vqStageFilter === s.key;
                  return (
                    <button key={s.key} type="button" data-testid={`vq-stat-${s.key}`}
                      onClick={() => setVqStageFilter(active ? null : s.key)}
                      className={`text-left rounded-xl border bg-card px-4 py-3 transition-all hover:border-sam-navy/40 ${active ? `ring-2 ${s.ring} border-transparent` : "border-border"}`}>
                      <div className={`text-2xl font-bold ${s.color}`} data-testid={`vq-stat-${s.key}-count`}>{s.count}</div>
                      <div className="text-xs font-semibold text-sam-slate uppercase tracking-wide">{s.label}</div>
                      <div className="text-[11px] text-sam-slate/70 mt-0.5" data-testid={`vq-stat-${s.key}-rate`}>{s.rate}</div>
                    </button>
                  );
                })}
              </div>
            );
          })()}
          {(() => {
            const goal = Number(monthlyGoal) || 0;
            const rev = Number(monthRevenue) || 0;
            const pctG = goal > 0 ? Math.min(100, Math.round((rev / goal) * 100)) : 0;
            return (
              <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="vq-goal-panel">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-display font-bold text-sam-navy"><Target className="h-4 w-4 text-sam-crimson" />This month's booked revenue</div>
                  {goalEdit ? (
                    <span className="flex items-center gap-1">
                      <span className="text-xs text-sam-slate">Goal $</span>
                      <Input type="number" min="0" className="h-7 w-28 text-xs" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} data-testid="goal-input" />
                      <Button size="sm" className="h-7 bg-sam-navy hover:bg-sam-navy/90" onClick={saveGoal} data-testid="goal-save">Save</Button>
                    </span>
                  ) : (
                    <button className="text-xs font-semibold text-sam-crimson hover:underline" onClick={() => { setGoalInput(String(goal)); setGoalEdit(true); }} data-testid="goal-edit">Edit goal</button>
                  )}
                </div>
                {pctG >= 100 && <div className="mb-2 rounded-md bg-sam-green/15 text-sam-green text-xs font-bold px-2 py-1.5 flex items-center gap-1.5" data-testid="goal-reached-banner"><Trophy className="h-3.5 w-3.5" />Monthly goal reached — ${rev.toLocaleString()} booked!</div>}
                <Progress value={pctG} className="h-2" data-testid="goal-progress" />
                <div className="flex items-center justify-between mt-1.5 text-xs">
                  <span className="font-semibold text-sam-green" data-testid="goal-current">${rev.toLocaleString()}</span>
                  <span className="text-sam-slate">{pctG}% of ${goal.toLocaleString()} goal</span>
                </div>
                <div className="mt-3 pt-3 border-t border-border flex items-center flex-wrap gap-x-2 gap-y-1 text-sm" data-testid="vq-pipeline">
                  <TrendingUp className="h-4 w-4 text-sam-navy" />
                  <span className="text-sam-slate">Pipeline:</span>
                  <span className="font-semibold text-sam-navy">{pipelineCount} call{pipelineCount === 1 ? "" : "s"} booked</span>
                  <span className="text-sam-slate">· ~${Math.round(Number(pipelineValue) || 0).toLocaleString()} in play</span>
                  {pipelineCount > 0 && <button onClick={() => setPipeOpen((o) => !o)} className="text-xs font-semibold text-sam-crimson hover:underline" data-testid="pipeline-toggle">{pipeOpen ? "Hide" : "By route"}</button>}
                </div>
                {pipeOpen && pipelineCount > 0 && (() => {
                  const booked = videoQueue.filter((v) => v.outreach_stage === "call_booked");
                  const byRoute = {};
                  booked.forEach((v) => { const k = v.route_code || "No route yet"; (byRoute[k] = byRoute[k] || []).push(v); });
                  return (
                    <div className="mt-2 space-y-1.5" data-testid="pipeline-breakdown">
                      {Object.entries(byRoute).sort((a, b) => b[1].length - a[1].length).map(([rc, items]) => (
                        <div key={rc} data-testid={`pipeline-route-${rc}`} className="text-xs">
                          <span className="font-semibold text-sam-navy">{rc}</span> <span className="text-sam-slate">· {items.length} booked{items[0].city ? ` · ${items[0].city}, ${items[0].state_abbr} ${items[0].zip}` : ""}</span>
                          <div className="text-sam-slate pl-3">{items.map((v) => v.business).join(", ")}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {revHistory.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border" data-testid="vq-goal-history">
                    <div className="text-xs font-semibold text-sam-slate mb-1.5">Booked revenue — last {revHistory.length} months</div>
                    <div className="flex items-end gap-1.5 h-16">
                      {(() => { const max = Math.max(1, ...revHistory.map((h) => h.total)); return revHistory.map((h) => (
                        <div key={h.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`rev-month-${h.month}`}>
                          <div className="w-full rounded-t bg-sam-green/70" style={{ height: `${Math.max(2, Math.round((h.total / max) * 48))}px` }} title={`$${Math.round(h.total).toLocaleString()}`}></div>
                          <span className="text-[9px] text-sam-slate">{h.month.slice(5)}</span>
                        </div>
                      )); })()}
                    </div>
                    {(() => {
                      if (revHistory.length < 2) return null;
                      const last = revHistory[revHistory.length - 1].total;
                      const prev = revHistory[revHistory.length - 2].total;
                      if (prev <= 0) return null;
                      const g = Math.round(((last - prev) / prev) * 100);
                      const up = g >= 0;
                      return <div className={`mt-1 text-[11px] font-semibold ${up ? "text-sam-green" : "text-sam-crimson"}`} data-testid="rev-trend">{up ? "▲" : "▼"} {Math.abs(g)}% vs last month</div>;
                    })()}
                  </div>
                )}
              </div>
            );
          })()}
          {(() => {
            const wonLeads = videoQueue.filter((v) => v.outreach_stage === "won")
              .sort((a, b) => ((b.won_at || b.created_at) || "").localeCompare((a.won_at || a.created_at) || "")).slice(0, 5);
            const now = Date.now();
            const daysSince = (s) => Math.floor((now - new Date(s).getTime()) / 86400000);
            const quietSince = (v) => { const a = v.outvid_sent_at || ""; const b = v.last_reminder_at || ""; return b > a ? b : a; };
            const quiet = videoQueue.filter((v) => v.outreach_stage === "sent_to_outvid" && quietSince(v) && daysSince(quietSince(v)) >= 3)
              .sort((a, b) => quietSince(a).localeCompare(quietSince(b))).slice(0, 6);
            if (wonLeads.length === 0 && quiet.length === 0) return null;
            return (
              <div className="grid md:grid-cols-2 gap-3 mb-4">
                {wonLeads.length > 0 && (
                  <div className="rounded-xl border border-sam-green/30 bg-sam-green/5 p-4" data-testid="vq-won-spotlight">
                    <div className="flex items-center gap-2 font-display font-bold text-sam-green mb-2"><Trophy className="h-4 w-4" />Recently won</div>
                    <ul className="space-y-1.5">
                      {wonLeads.map((v) => (
                        <li key={v.id} data-testid={`vq-won-${v.id}`} className="text-sm text-sam-navy flex items-center justify-between gap-2">
                          <span className="truncate"><span className="font-semibold">{v.business}</span> <span className="text-sam-slate">· {v.category}{v.route_code ? ` · ${v.route_code}` : ""}</span></span>
                          <span className="flex items-center gap-2 whitespace-nowrap">
                            {v.won_amount > 0 && <span className="text-xs font-semibold text-sam-green" data-testid={`vq-won-amt-${v.id}`}>${Number(v.won_amount).toLocaleString()}</span>}
                            <button className="text-sam-slate hover:text-sam-crimson" onClick={() => setWonDialog({ open: true, id: v.id, amount: v.won_amount ? String(v.won_amount) : "", edit: true })} data-testid={`vq-won-edit-${v.id}`} title="Edit sale amount"><Pencil className="h-3.5 w-3.5" /></button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {quiet.length > 0 && (
                  <div className="rounded-xl border border-sam-amber/40 bg-sam-amber/5 p-4" data-testid="vq-followup-nudges">
                    <div className="flex items-center gap-2 font-display font-bold text-sam-amber mb-2"><BellRing className="h-4 w-4" />Needs follow-up</div>
                    <ul className="space-y-1.5">
                      {quiet.map((v) => (
                        <li key={v.id} data-testid={`vq-nudge-${v.id}`} className="text-sm text-sam-navy flex items-center justify-between gap-2">
                          <span className="truncate"><span className="font-semibold">{v.business}</span> <span className="text-sam-slate">· {v.category}</span></span>
                          <span className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-xs text-sam-crimson font-semibold">{daysSince(quietSince(v))}d quiet</span>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => sendReminder(v.id)} data-testid={`vq-remind-${v.id}`}>Send reminder</Button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
          {(() => {
            const lost = videoQueue.filter((v) => v.outreach_stage === "lost");
            if (lost.length === 0) return null;
            const byReason = {};
            lost.forEach((v) => { const k = v.lost_reason || "Unspecified"; byReason[k] = (byReason[k] || 0) + 1; });
            const sorted = Object.entries(byReason).sort((a, b) => b[1] - a[1]);
            const top = sorted[0];
            return (
              <div className="rounded-xl border border-slate-300 bg-slate-50 p-4 mb-4" data-testid="vq-lost-insights">
                <div className="flex items-center gap-2 font-display font-bold text-slate-500 mb-2"><X className="h-4 w-4" />Why deals are lost ({lost.length})</div>
                {top && <p className="text-xs text-slate-500 mb-2">Top objection: <span className="font-semibold text-slate-700" data-testid="lost-top-reason">{top[0]}</span> ({top[1]})</p>}
                <ul className="space-y-1.5">
                  {sorted.map(([reason, count]) => (
                    <li key={reason} data-testid={`lost-reason-row-${reason.replace(/\s+/g, "-").toLowerCase()}`} className="text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-40 shrink-0 truncate text-slate-600">{reason}</span>
                        <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-slate-400" style={{ width: `${Math.round((count / lost.length) * 100)}%` }}></div></div>
                        <span className="w-6 text-right text-xs font-semibold text-slate-500">{count}</span>
                      </div>
                      {LOST_TIPS[reason] && <p className="mt-0.5 mb-1 text-[11px] text-slate-400 pl-0.5" data-testid={`lost-tip-${reason.replace(/\s+/g, "-").toLowerCase()}`}><span className="font-semibold text-slate-500">Tip:</span> {LOST_TIPS[reason]}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
          {(() => {
            const rank = { new: 0, sent_to_outvid: 1, video_watched: 2, call_booked: 3, won: 4, lost: -1 };
            const r = (v) => rank[v.outreach_stage || "new"] ?? 0;
            const thr = { sent: 1, watched: 2, booked: 3, won: 4 };
            const fLabel = { sent: "Sent", watched: "Watched", booked: "Call Booked", won: "Won" };
            const shown = vqStageFilter ? videoQueue.filter((v) => r(v) >= thr[vqStageFilter]) : (vqUnsent ? videoQueue.filter((v) => !v.outvid_sent) : videoQueue);
            const dupMap = {};
            videoQueue.forEach((v) => { if (v.route_code && v.outreach_stage !== "lost") { const k = `${v.route_code}||${v.category}`; dupMap[k] = (dupMap[k] || 0) + 1; } });
            const isDup = (v) => v.route_code && v.outreach_stage !== "lost" && dupMap[`${v.route_code}||${v.category}`] > 1;
            return (
              <div className="space-y-2" data-testid="admin-video-queue">
                {vqStageFilter && (
                  <div className="flex items-center gap-2 text-sm mb-1" data-testid="vq-filter-bar">
                    <span className="text-sam-slate">Showing <span className="font-semibold text-sam-navy">{fLabel[vqStageFilter]}</span> ({shown.length})</span>
                    <button onClick={() => setVqStageFilter(null)} className="text-xs font-semibold text-sam-crimson hover:underline" data-testid="vq-filter-clear">Clear filter</button>
                  </div>
                )}
                {shown.length === 0 && <p className="text-sm text-sam-slate py-4" data-testid="vq-empty">No leads{vqStageFilter ? ` at "${fLabel[vqStageFilter]}"` : (vqUnsent ? " waiting to be sent" : " in the video queue")}.</p>}
                {shown.map((v) => (
                  <div key={v.id} data-testid={`vq-row-${v.id}`} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-display font-bold text-sam-navy">
                          <Phone className="h-4 w-4 text-sam-crimson" />{v.business}
                          {v.owner_name && v.owner_name !== v.business && <span className="text-sm font-normal text-sam-slate">· {v.owner_name}</span>}
                        </div>
                        <div className="text-sm text-sam-slate mt-0.5 flex flex-wrap gap-x-3">
                          {v.email && <a href={`mailto:${v.email}`} className="hover:text-sam-crimson">{v.email}</a>}
                          {v.phone && <a href={`tel:${v.phone}`} className="hover:text-sam-crimson">{v.phone}</a>}
                          {v.website && <a href={v.website.startsWith("http") ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer" className="hover:text-sam-crimson">{v.website}</a>}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline">{v.category}</Badge>
                        {isDup(v) && <Badge className="bg-sam-crimson" data-testid={`vq-dup-${v.id}`}><AlertTriangle className="h-3 w-3 mr-1" />Dup trade · {v.route_code}</Badge>}
                        {(v.city || v.zip) && <Badge className="bg-sam-navy">{[v.city, v.state_abbr].filter(Boolean).join(", ")} {v.zip}</Badge>}
                        {v.outvid_sent && <Badge className="bg-sam-amber">Sent {v.outvid_sent_at ? new Date(v.outvid_sent_at).toLocaleDateString() : ""}</Badge>}
                        <Select value={v.outreach_stage || "new"} onValueChange={(val) => { if (val === "won") setWonDialog({ open: true, id: v.id, amount: "", edit: false }); else if (val === "lost") setLostDialog({ open: true, id: v.id }); else setOutreachStage(v.id, val); }}>
                          <SelectTrigger className={`h-7 w-[140px] text-xs font-semibold border-0 text-white ${OUTREACH_COLOR[v.outreach_stage || "new"]}`} data-testid={`vq-stage-${v.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{OUTREACH_STAGES.map((s) => <SelectItem key={s} value={s}>{OUTREACH_LABEL[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className={`mt-2 rounded-md px-2 py-1 text-xs font-semibold inline-block ${v.category_open ? "bg-sam-green/10 text-sam-green" : "bg-sam-navy/5 text-sam-slate"}`} data-testid={`vq-route-${v.id}`}>{v.route_status}</div>
                    {(() => { const t = v.stage_changed_at || v.created_at; if (!t) return null; const d = Math.floor((Date.now() - new Date(t).getTime()) / 86400000); const cls = d >= 7 ? "text-sam-crimson" : d >= 4 ? "text-sam-amber" : "text-sam-slate"; return <span className={`ml-2 text-[11px] font-semibold ${cls}`} data-testid={`vq-age-${v.id}`}>{d}d in {OUTREACH_LABEL[v.outreach_stage || "new"]}</span>; })()}
                    {v.outreach_stage === "lost" && v.lost_reason && <span className="ml-2 text-[11px] font-semibold text-slate-400" data-testid={`vq-lost-reason-${v.id}`}>Lost: {v.lost_reason}</span>}
                    <div className="mt-2 flex items-start gap-2">
                      <p className="flex-1 text-sm text-sam-navy/80 italic bg-sam-navy/5 rounded-md px-3 py-2" data-testid={`vq-script-${v.id}`}>{v.script_line}</p>
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(v.script_line); toast.success("Script copied"); }} data-testid={`vq-copy-${v.id}`}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
                      {!v.outvid_sent && <Button size="sm" variant="outline" onClick={() => markSentOutvid(v.id)} data-testid={`vq-mark-${v.id}`}>Mark sent</Button>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          <Dialog open={wonDialog.open} onOpenChange={(o) => setWonDialog((d) => ({ ...d, open: o }))}>
            <DialogContent data-testid="won-amount-dialog">
              <DialogHeader><DialogTitle>{wonDialog.edit ? "Edit sale amount" : "Mark deal as Won"}</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Label className="text-xs">Sale amount ($)</Label>
                <Input type="number" min="0" step="1" placeholder="e.g. 1800" value={wonDialog.amount} onChange={(e) => setWonDialog((d) => ({ ...d, amount: e.target.value }))} data-testid="won-amount-input" autoFocus />
                <p className="text-xs text-sam-slate">Enter the contract size so your booked-revenue tally reflects real deal values. Leave blank to skip.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setWonDialog({ open: false, id: null, amount: "", edit: false })} data-testid="won-cancel">Cancel</Button>
                <Button className="bg-sam-green hover:bg-sam-green/90" onClick={confirmWon} data-testid="won-confirm">{wonDialog.edit ? "Save amount" : "Mark Won"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={lostDialog.open} onOpenChange={(o) => setLostDialog((d) => ({ ...d, open: o }))}>
            <DialogContent data-testid="lost-reason-dialog">
              <DialogHeader><DialogTitle>Why was this deal lost?</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-2 py-2">
                {LOST_REASONS.map((reason) => (
                  <Button key={reason} variant="outline" className="justify-start text-sm" onClick={() => confirmLost(reason)} data-testid={`lost-reason-${reason.replace(/\s+/g, "-").toLowerCase()}`}>{reason}</Button>
                ))}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setLostDialog({ open: false, id: null })} data-testid="lost-cancel">Cancel</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="crm" className="pt-4">
          {subaccounts.length === 0 ? <p className="text-sam-slate text-sm">No CRM sub-accounts yet. One is auto-created in Zuops when a business makes its first paid purchase.</p> : (
            <div className="space-y-2" data-testid="admin-subaccounts">
              <p className="text-sm text-sam-slate mb-2">Zuops sub-accounts auto-provisioned for paid business owners — newest first.</p>
              {subaccounts.map((b) => (
                <div key={b.id} data-testid={`subaccount-row-${b.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-4">
                  <div>
                    <div className="flex items-center gap-2 font-display font-bold text-sam-navy">
                      <Sparkles className="h-4 w-4 text-sam-green" />{b.name}
                      <span className="inline-flex items-center rounded-full bg-sam-green/10 text-sam-green text-xs font-semibold px-2 py-0.5">active</span>
                    </div>
                    <div className="text-sm text-sam-slate mt-0.5">
                      {[b.city, b.state_abbr].filter(Boolean).join(", ")}
                      {b.zuops_invite_email && <span className="ml-2"><Mail className="h-3 w-3 inline mr-1" />{b.zuops_invite_email}</span>}
                    </div>
                    <div className="text-xs text-sam-slate/70 mt-0.5">Workspace {b.zuops_workspace_id}{b.zuops_provisioned_at ? ` · ${new Date(b.zuops_provisioned_at).toLocaleDateString()}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" asChild data-testid={`subaccount-view-${b.id}`}><Link to={`/business/${b.slug}`}>View listing</Link></Button>
                    <Button variant="outline" size="sm" onClick={() => resendInvite(b.id)} data-testid={`subaccount-resend-${b.id}`}><Mail className="h-3.5 w-3.5 mr-1" />Resend invite</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inbox" className="pt-4 space-y-4">
          <QuietHoursPanel />
          <SmsInbox onActivity={refreshUnread} />
        </TabsContent>

        <TabsContent value="seo" className="pt-4">
          <SeoBlogPanel />
        </TabsContent>

        <TabsContent value="plans" className="pt-4 space-y-6">
          <PlansPanel onCount={setPlanFailed} />
          <CreditBudgetsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SmsInbox({ onActivity }) {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [activeWs, setActiveWs] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [lead, setLead] = useState(null);
  const [optedOut, setOptedOut] = useState(false);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const loadConvos = async () => {
    try { const { data } = await api.get("/admin/sms/conversations"); setConvos(data.conversations || []); }
    catch { toast.error("Could not load conversations"); }
  };
  useEffect(() => { loadConvos(); }, []);
  const open = async (c) => {
    const lid = c.lead_id || c.lead?.id || c.id;
    const ws = c.workspace_id || activeWs || "";
    setActive(lid); setActiveWs(ws); setLoading(true);
    try {
      const { data } = await api.get(`/admin/sms/conversation/${lid}${ws ? `?workspace_id=${encodeURIComponent(ws)}` : ""}`);
      setMsgs(data.messages || []); setLead(data.lead || null); setOptedOut(!!data.opted_out);
      onActivity?.();
    } catch { toast.error("Could not load conversation"); } finally { setLoading(false); }
  };
  const send = async () => {
    if (!reply.trim() || !active) return;
    try { const { data } = await api.post("/admin/sms/send", { lead_id: active, body: reply, workspace_id: activeWs }); if (data.ok) { toast.success("Text sent"); setReply(""); open({ lead_id: active, workspace_id: activeWs }); } else toast.error("Send failed"); }
    catch { toast.error("Send failed"); }
  };
  const optout = async () => {
    try { await api.post("/admin/sms/optout", { lead_id: active, phone: lead?.phone, workspace_id: activeWs }); setOptedOut(true); toast.success("Marked do-not-contact"); }
    catch { toast.error("Could not opt out"); }
  };
  const draft = async () => {
    if (!active) return;
    setDrafting(true);
    try { const { data } = await api.post("/admin/sms/draft", { lead_id: active, workspace_id: activeWs }); setReply(data.draft || ""); }
    catch { toast.error("Could not draft a reply"); } finally { setDrafting(false); }
  };
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="sms-inbox">
      <div className="md:col-span-1 space-y-2">
        <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sam-navy">Conversations</h3><Button size="sm" variant="outline" onClick={loadConvos} data-testid="inbox-refresh">Refresh</Button></div>
        {convos.length === 0 && <p className="text-sm text-sam-slate">No conversations yet.</p>}
        {convos.map((c) => {
          const lid = c.lead_id || c.lead?.id || c.id;
          const nm = c.lead?.full_name || c.full_name || c.name || c.lead?.phone || "Lead";
          return (
            <button key={lid} onClick={() => open(c)} data-testid={`inbox-convo-${lid}`}
              className={`w-full text-left rounded-lg border p-2.5 text-sm transition ${active === lid ? "border-sam-crimson bg-sam-crimson/5" : "border-border hover:border-sam-navy/40"}`}>
              <div className="font-semibold text-sam-navy truncate">{nm}</div>
              <div className="text-xs text-sam-slate truncate">{(typeof c.last_message === "string" ? c.last_message : c.last_message?.body) || c.preview || c.lead?.phone || ""}</div>
            </button>
          );
        })}
      </div>
      <div className="md:col-span-2 rounded-xl border border-border bg-card p-4 min-h-[300px] flex flex-col">
        {!active ? <p className="text-sm text-sam-slate m-auto">Select a conversation to view and reply.</p> : (
          <>
            <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
              <div className="font-semibold text-sam-navy">{lead?.full_name || lead?.phone || "Lead"}<span className="ml-2 text-xs font-normal text-sam-slate">{lead?.phone}</span></div>
              {optedOut ? <Badge className="bg-slate-400" data-testid="inbox-optedout">Do-not-contact</Badge>
                : <Button size="sm" variant="outline" onClick={optout} data-testid="inbox-optout">Opt out</Button>}
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto max-h-[360px]" data-testid="inbox-messages">
              {loading ? <Loader2 className="h-5 w-5 animate-spin text-sam-slate" /> : [...msgs].reverse().map((m) => {
                const out = m.event_type === "sms_outbound";
                return <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${out ? "ml-auto bg-sam-navy text-sam-cream" : "bg-slate-100 text-sam-navy"}`} data-testid={`inbox-msg-${m.id}`}>{m.body}</div>;
              })}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" onClick={draft} disabled={optedOut || drafting} data-testid="inbox-draft" title="AI draft reply">{drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}</Button>
              <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={optedOut ? "Lead opted out" : "Type a reply…"} disabled={optedOut} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="inbox-reply-input" />
              <Button onClick={send} disabled={optedOut || !reply.trim()} className="bg-sam-crimson hover:bg-sam-crimson/90" data-testid="inbox-send">Send</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReferralLedger() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ referrer_name: "", referrer_phone: "", referred_name: "", referred_phone: "" });
  const load = async () => { try { const { data } = await api.get("/admin/referrals"); setItems(data || []); } catch { /* noop */ } };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.referrer_phone || !form.referred_phone) { toast.error("Referrer and referred phone required"); return; }
    try { await api.post("/admin/referrals", form); toast.success("Referral logged — auto-credits when they buy"); setForm({ referrer_name: "", referrer_phone: "", referred_name: "", referred_phone: "" }); load(); }
    catch { toast.error("Could not save"); }
  };
  const convert = async (id) => { try { const { data } = await api.post(`/admin/referrals/${id}/convert`); toast.success(`Credited ${data.credited}${data.texted ? " + texted" : ""}`); load(); } catch { toast.error("Could not convert"); } };
  const color = { pending: "bg-sam-amber", credited: "bg-sam-green" };
  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="referral-ledger">
      <h3 className="font-display font-bold text-sam-navy mb-2">Referral rewards</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        <Input value={form.referrer_name} onChange={(e) => setForm({ ...form, referrer_name: e.target.value })} placeholder="Referrer name" data-testid="ref-referrer-name" />
        <Input value={form.referrer_phone} onChange={(e) => setForm({ ...form, referrer_phone: e.target.value })} placeholder="Referrer phone" data-testid="ref-referrer-phone" />
        <Input value={form.referred_name} onChange={(e) => setForm({ ...form, referred_name: e.target.value })} placeholder="Referred name" data-testid="ref-referred-name" />
        <Input value={form.referred_phone} onChange={(e) => setForm({ ...form, referred_phone: e.target.value })} placeholder="Referred phone" data-testid="ref-referred-phone" />
      </div>
      <Button size="sm" className="bg-sam-navy hover:bg-sam-navy/90 mb-3" onClick={create} data-testid="ref-add">Log referral</Button>
      <p className="text-[11px] text-sam-slate mb-2">When the referred business becomes a paid ad buyer, the referrer is auto-credited and texted.</p>
      <ul className="space-y-1.5">
        {items.map((r) => (
          <li key={r.id} data-testid={`ref-row-${r.id}`} className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate"><span className="font-semibold text-sam-navy">{r.referrer_name || r.referrer_phone}</span> <span className="text-sam-slate">→ {r.referred_name || r.referred_phone} · {r.perk}</span></span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <Badge className={color[r.status] || "bg-slate-400"}>{r.status}</Badge>
              {r.status === "pending" && <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => convert(r.id)} data-testid={`ref-convert-${r.id}`}>Mark converted</Button>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const QH_STATES = ["HI", "AK", "CA", "OR", "WA", "NV", "AZ", "MT", "ID", "WY", "UT", "CO", "NM", "ND", "SD", "NE", "KS", "OK", "TX", "MN", "IA", "MO", "AR", "LA", "WI", "IL", "MS", "AL", "TN", "NY", "FL", "GA", "NC", "SC", "VA", "OH", "MI", "PA", "MA", "NJ"];
const qhHour = (h) => { const hr = ((h + 11) % 12) + 1; const ap = (h % 24) < 12 ? "am" : "pm"; return `${hr}${h === 24 ? "am" : ap}`; };

function QuietHoursPanel() {
  const [cfg, setCfg] = useState({ start: 8, end: 21, overrides: {} });
  const [stateSel, setStateSel] = useState("CA");
  const [ov, setOv] = useState({ start: 9, end: 20 });
  useEffect(() => { api.get("/admin/quiet-hours").then(({ data }) => setCfg({ start: data.start ?? 8, end: data.end ?? 21, overrides: data.overrides || {} })).catch(() => {}); }, []);
  const save = async (next) => {
    const body = next || cfg;
    try { await api.put("/admin/quiet-hours", body); setCfg(body); toast.success("Quiet hours saved"); }
    catch { toast.error("Could not save"); }
  };
  const addOverride = () => save({ ...cfg, overrides: { ...cfg.overrides, [stateSel]: { start: Number(ov.start), end: Number(ov.end) } } });
  const removeOverride = (st) => { const nextOv = { ...cfg.overrides }; delete nextOv[st]; save({ ...cfg, overrides: nextOv }); };
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="quiet-hours-panel">
      <h3 className="font-display font-bold text-sam-navy mb-1 flex items-center gap-2"><BellRing className="h-4 w-4 text-sam-crimson" />Quiet hours</h3>
      <p className="text-[11px] text-sam-slate mb-3">Outreach texts are held outside these hours (in each lead's local timezone) and auto-sent when daytime begins. Operator alerts are exempt.</p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="text-xs font-semibold text-sam-navy block mb-1">Global start</label>
          <select value={cfg.start} onChange={(e) => setCfg({ ...cfg, start: Number(e.target.value) })} className="rounded-md border border-sam-navy/20 px-3 py-1.5 text-sm" data-testid="qh-global-start">
            {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{qhHour(i)}</option>)}
          </select></div>
        <div><label className="text-xs font-semibold text-sam-navy block mb-1">Global end</label>
          <select value={cfg.end} onChange={(e) => setCfg({ ...cfg, end: Number(e.target.value) })} className="rounded-md border border-sam-navy/20 px-3 py-1.5 text-sm" data-testid="qh-global-end">
            {Array.from({ length: 24 }, (_, i) => <option key={i + 1} value={i + 1}>{qhHour(i + 1)}</option>)}
          </select></div>
        <Button size="sm" className="bg-sam-navy hover:bg-sam-navy/90" onClick={() => save()} data-testid="qh-save">Save window</Button>
      </div>
      <div className="border-t border-border pt-3">
        <div className="text-xs font-bold uppercase tracking-wider text-sam-slate mb-2">Per-state overrides</div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <select value={stateSel} onChange={(e) => setStateSel(e.target.value)} className="rounded-md border border-sam-navy/20 px-3 py-1.5 text-sm" data-testid="qh-state">
            {QH_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={ov.start} onChange={(e) => setOv({ ...ov, start: Number(e.target.value) })} className="rounded-md border border-sam-navy/20 px-3 py-1.5 text-sm" data-testid="qh-ov-start">
            {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{qhHour(i)}</option>)}
          </select>
          <span className="text-sm text-sam-slate">to</span>
          <select value={ov.end} onChange={(e) => setOv({ ...ov, end: Number(e.target.value) })} className="rounded-md border border-sam-navy/20 px-3 py-1.5 text-sm" data-testid="qh-ov-end">
            {Array.from({ length: 24 }, (_, i) => <option key={i + 1} value={i + 1}>{qhHour(i + 1)}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={addOverride} data-testid="qh-add-override">Add override</Button>
        </div>
        <ul className="space-y-1.5">
          {Object.entries(cfg.overrides).map(([st, w]) => (
            <li key={st} className="flex items-center justify-between text-sm" data-testid={`qh-override-${st}`}>
              <span className="text-sam-navy"><span className="font-semibold">{st}</span>: {qhHour(w.start)} – {qhHour(w.end)}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeOverride(st)} data-testid={`qh-remove-${st}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </li>
          ))}
          {Object.keys(cfg.overrides).length === 0 && <li className="text-xs text-sam-slate">No overrides — all states use the global window.</li>}
        </ul>
      </div>
    </div>
  );
}

function ReferralLeaderboard() {
  const [board, setBoard] = useState([]);
  useEffect(() => { api.get("/admin/referrals/leaderboard").then(({ data }) => setBoard(data.leaderboard || [])).catch(() => {}); }, []);
  if (board.length === 0) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="referral-leaderboard">
      <h3 className="font-display font-bold text-sam-navy mb-2 flex items-center gap-2"><Trophy className="h-4 w-4 text-sam-amber" />Top referrers</h3>
      <ul className="space-y-1.5">
        {board.slice(0, 10).map((r, i) => (
          <li key={(r.referrer_phone || r.referrer_name || "x") + i} data-testid={`leaderboard-row-${i}`} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 truncate">
              <span className={`w-6 text-center font-bold ${i === 0 ? "text-sam-amber" : "text-sam-slate"}`}>{i + 1}</span>
              <span className="font-semibold text-sam-navy truncate">{r.referrer_name || r.referrer_phone || "Unknown"}</span>
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <Badge className="bg-sam-green">{r.credited} credited</Badge>
              {r.pending > 0 && <Badge className="bg-sam-amber">{r.pending} pending</Badge>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlansPanel({ onCount }) {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [testing, setTesting] = useState(false);
  const [zuops, setZuops] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/plans/signups").then(({ data }) => {
      const s = data.signups || [];
      setSignups(s);
      onCount?.(s.filter((x) => x.provisioning_status === "failed").length);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  const testZuops = async () => {
    setTesting(true); setZuops(null);
    try { const { data } = await api.get("/admin/plans/zuops-test"); setZuops(data); data.ok ? toast.success("Zuops connection OK") : toast.error("Zuops connection failed"); }
    catch { setZuops({ ok: false, error: "request failed" }); toast.error("Zuops test failed"); }
    finally { setTesting(false); }
  };

  const retry = async (id) => {
    setRetrying(id);
    try { const { data } = await api.post(`/admin/plans/${id}/retry`); data.ok ? toast.success("Provisioned") : toast.error(`Retry failed: ${data?.result?.error || "error"}`); load(); }
    catch { toast.error("Retry failed"); } finally { setRetrying(null); }
  };

  const provBadge = (s) => {
    const map = { created: "bg-sam-green", created_invite_failed: "bg-sam-amber", pending: "bg-slate-400", provisioning: "bg-sam-navy", failed: "bg-sam-crimson" };
    const label = s === "created_invite_failed" ? "created – invite failed" : (s || "pending");
    return <Badge className={map[s] || "bg-slate-400"}>{label}</Badge>;
  };
  const acctBadge = (s) => {
    if (s === "cancelled_review") return <Badge className="bg-sam-crimson">cancelled — review</Badge>;
    if (s === "active") return <Badge className="bg-sam-green">active</Badge>;
    return <Badge variant="outline">{s || "pending"}</Badge>;
  };

  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="plans-panel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-sam-navy">Plan customers & sub-accounts</h3>
          <p className="text-[11px] text-sam-slate">Subscriptions and their automatic client dashboard provisioning.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={testZuops} disabled={testing} data-testid="zuops-test-btn">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Test Zuops connection
          </Button>
          <Button size="sm" variant="outline" onClick={load} data-testid="plans-refresh">Refresh</Button>
        </div>
      </div>
      {zuops && (
        <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${zuops.ok ? "border-sam-green/40 bg-sam-green/10 text-sam-navy" : "border-sam-crimson/40 bg-sam-crimson/10 text-sam-crimson"}`} data-testid="zuops-test-result">
          {zuops.ok
            ? <>Connected to {zuops.endpoint}{typeof zuops.workspace_count === "number" ? ` · ${zuops.workspace_count} workspaces visible` : ""}.</>
            : <>Connection failed{zuops.key_present === false ? " (no API key configured)" : ""}: {zuops.error || "unknown error"}</>}
        </div>
      )}
      {signups.length === 0 ? (
        <p className="text-sm text-sam-slate">No plan signups yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-sam-slate border-b border-border">
              <th className="py-2 pr-3">Business</th><th className="py-2 pr-3">Tier</th><th className="py-2 pr-3">Stripe</th>
              <th className="py-2 pr-3">Account</th><th className="py-2 pr-3">Provisioning</th><th className="py-2 pr-3">Workspace</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {signups.map((s) => (
                <tr key={s.id} className="border-b border-border/60" data-testid={`plan-signup-${s.id}`}>
                  <td className="py-2.5 pr-3">
                    <div className="font-semibold text-sam-navy">{s.business_name}</div>
                    <div className="text-xs text-sam-slate">{s.email}</div>
                  </td>
                  <td className="py-2.5 pr-3"><Badge className="bg-sam-navy capitalize">{s.plan}</Badge></td>
                  <td className="py-2.5 pr-3 text-sam-slate">{s.stripe_status}</td>
                  <td className="py-2.5 pr-3">{acctBadge(s.account_status)}</td>
                  <td className="py-2.5 pr-3">{provBadge(s.provisioning_status)}{(s.error || s.invite_error) && <div className="text-[11px] text-sam-crimson mt-0.5">{s.error || s.invite_error}</div>}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-sam-slate">
                    {s.workspace_id || "—"}
                    {s.business_slug && <a href={`/business/${s.business_slug}`} target="_blank" rel="noreferrer" className="block text-sam-crimson font-sans font-semibold not-italic mt-0.5" data-testid={`plan-listing-${s.id}`}>/business/{s.business_slug}</a>}
                  </td>
                  <td className="py-2.5">
                    {!["created"].includes(s.provisioning_status) && s.stripe_status === "paid" && (
                      <Button size="sm" variant="outline" onClick={() => retry(s.id)} disabled={retrying === s.id} data-testid={`plan-retry-${s.id}`}>
                        {retrying === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Retry"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReferlySettings() {
  const [d, setD] = useState(null);
  const [lb, setLb] = useState(null);
  const [form, setForm] = useState({ affiliate_id: "", email: "", name: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/referly").then(({ data }) => setD(data)).catch(() => {});
  const loadLb = () => api.get("/admin/referly/leaderboard").then(({ data }) => setLb(data.affiliates || [])).catch(() => setLb([]));
  useEffect(() => { load(); loadLb(); }, []);
  const toggleAuto = async () => { try { await api.put("/admin/referly", { auto_report: !d.auto_report }); toast.success("Saved"); load(); } catch { toast.error("Failed"); } };
  const report = async () => {
    if (!form.affiliate_id.trim() || !form.amount) return toast.error("Affiliate code and amount are required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/referly/report-sale", { affiliate_id: form.affiliate_id.trim(), email: form.email.trim(), name: form.name.trim(), amount: parseFloat(form.amount) });
      if (data.sale_id) toast.success(`Reported — Referly sale #${data.sale_id}`);
      else toast.info("Sent, but Referly didn't attribute it — check the affiliate code is correct");
      setForm({ affiliate_id: "", email: "", name: "", amount: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not report sale"); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="referly-settings">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-display font-bold text-sam-navy">Affiliate sales tracking (Referly)</h3>
        <Badge className={d.key_present ? "bg-sam-green" : "bg-sam-crimson"} data-testid="referly-key-status">{d.key_present ? "API key connected" : "No API key"}</Badge>
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-sam-navy mb-3" data-testid="referly-auto-toggle">
        <input type="checkbox" className="accent-sam-crimson" checked={!!d.auto_report} onChange={toggleAuto} />
        Also report Stripe sales to Referly from our server (backup)
      </label>
      <p className="text-[11px] text-sam-slate mb-3">Leave OFF if you've connected Referly's Stripe app (recommended) — otherwise sales may be counted twice. Turn ON only if you did not connect Referly in Stripe.</p>
      <div className="rounded-lg bg-slate-50 border border-border/60 p-3">
        <p className="text-xs font-semibold text-sam-navy mb-2">Report an offline / invoice sale</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
          <Input value={form.affiliate_id} onChange={(e) => setForm({ ...form, affiliate_id: e.target.value })} placeholder="Affiliate code" data-testid="referly-report-affiliate" />
          <Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount (e.g. 10800)" data-testid="referly-report-amount" />
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Customer name" data-testid="referly-report-name" />
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Customer email" data-testid="referly-report-email" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" className="bg-sam-navy hover:bg-sam-navy/90" onClick={report} disabled={busy} data-testid="referly-report-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Report sale to Referly"}</Button>
          <Button size="sm" variant="outline" onClick={() => setForm({ ...form, amount: "12500" })} data-testid="referly-fullcard-btn" title="1 Premium + 2 Standard + 9 Basic = $12,500 per drop">Full 12-spot card ($12,500)</Button>
        </div>
      </div>
      {(d.recent_sales || []).length > 0 && (
        <div className="mt-3 space-y-1 text-xs max-h-40 overflow-y-auto" data-testid="referly-recent-sales">
          {d.recent_sales.map((s, i) => (
            <div key={i} className="flex items-center justify-between gap-2 border-b border-border/50 pb-1">
              <span className="text-sam-navy">{s.affiliate_id || s.promo_code || "—"} · ${s.amount}</span>
              <span className={s.attributed ? "text-sam-green" : "text-sam-slate"}>{s.attributed ? `sale #${s.sale_id}` : (s.status === 200 ? "not attributed" : `error ${s.status}`)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 border-t border-border pt-3" data-testid="referly-leaderboard">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-bold text-sam-navy text-sm">Sales leaderboard</h4>
          <Button size="sm" variant="outline" onClick={loadLb} data-testid="referly-lb-refresh">Refresh</Button>
        </div>
        {lb === null ? <Loader2 className="h-4 w-4 animate-spin text-sam-slate" /> : lb.length === 0 ? (
          <p className="text-xs text-sam-slate">No affiliates in Referly yet. Invite staff (they're auto-added) or add them in your Referly dashboard.</p>
        ) : (
          <div className="space-y-1 text-xs max-h-56 overflow-y-auto">
            {lb.map((a, i) => (
              <div key={a.email || i} className="flex items-center justify-between gap-2 border-b border-border/50 pb-1" data-testid={`referly-lb-row-${i}`}>
                <span className="text-sam-navy truncate"><b>#{i + 1}</b> {a.name}{a.code && <span className="text-sam-slate"> · {a.code}</span>}</span>
                <span className="text-sam-slate shrink-0">{a.referrals} ref · {a.clicks} clicks · <span className="text-sam-green font-semibold">${Number(a.earned).toFixed(2)}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RepRanks() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/team/ranks").then(({ data }) => setD(data)).catch(() => setD({ reps: [] }));
  useEffect(() => { load(); }, []);
  const ack = async (uid, rank_key) => { try { await api.post("/admin/team/ranks/ack", { user_id: uid, rank_key }); toast.success("Marked — switch the plan in Referly"); load(); } catch { toast.error("Failed"); } };
  if (!d) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="rep-ranks">
      <h3 className="font-display font-bold text-sam-navy mb-1">Rep ranks &amp; commission</h3>
      <p className="text-xs text-sam-slate mb-3">Rank = active cards (10+/12 paid). Associate 9% · Manager 13.5% · Senior 18% · Director 22.5%. Overrides (paid in Referly): L1 15% / L2 5%, need 4+ active cards. Switch commission plans in Referly by hand.</p>
      {(!d.reps || d.reps.length === 0) ? <p className="text-sm text-sam-slate">No reps with an affiliate code yet. Add codes under Team members below.</p> : (
        <div className="space-y-2">
          {d.reps.map((r) => (
            <div key={r.user_id} className="flex items-center justify-between gap-3 flex-wrap border-b border-border/50 pb-2" data-testid={`rep-rank-${r.user_id}`}>
              <div className="text-sm"><span className="font-semibold text-sam-navy">{r.name || r.email}</span> <span className="text-sam-slate">· {r.referly_code || "no code"}</span></div>
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="text-sam-slate">{r.active_cards} active cards</span>
                <Badge className="bg-sam-navy">{r.rank} · {r.commission_pct}%</Badge>
                {r.override_eligible && <Badge className="bg-sam-green">override ✓</Badge>}
                {r.rank_history?.length > 1 && <span className="text-sam-slate">rank changed {String(r.rank_history[r.rank_history.length - 1].at).slice(0, 10)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AffiliatesSection() {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/affiliates").then(({ data }) => setList(data.affiliates || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const toggle = async (a) => {
    try {
      await api.post(`/admin/affiliates/${a.user_id}/activate`, { active: !a.affiliate_active });
      toast.success(!a.affiliate_active ? "Commissions approved" : "Commissions turned off");
      load();
    } catch { toast.error("Could not update"); }
  };
  if (!list) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="affiliates-section">
      <h3 className="font-display font-bold text-sam-navy mb-1">Affiliates</h3>
      <p className="text-[11px] text-sam-slate mb-3">Approve commissions for self-serve affiliates (bypasses the interview). Interview-completed affiliates are already eligible.</p>
      {list.length === 0 ? <p className="text-sm text-sam-slate">No self-serve affiliates yet.</p> : (
        <div className="space-y-2">
          {list.map((a) => (
            <div key={a.user_id} className="flex items-center justify-between flex-wrap gap-2 border-b border-border/50 pb-2" data-testid={`affiliate-row-${a.user_id}`}>
              <div className="min-w-0">
                <span className="font-semibold text-sam-navy">{a.name || a.email}</span> <span className="text-xs text-sam-slate">{a.email}</span>
                <div className="text-[11px] text-sam-slate">Code {a.referly_code} · {a.active_cards} active card{a.active_cards === 1 ? "" : "s"}{a.interview_completed ? " · interview ✓" : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={a.affiliate_active ? "bg-sam-green" : a.interview_completed ? "bg-sam-navy" : "bg-slate-400"}>{a.affiliate_active ? "Commissions on" : a.interview_completed ? "Eligible (interview)" : "Not approved"}</Badge>
                <Button size="sm" variant={a.affiliate_active ? "outline" : "default"} className={a.affiliate_active ? "text-sam-crimson" : "bg-sam-green hover:bg-sam-green/90"} onClick={() => toggle(a)} data-testid={`affiliate-toggle-${a.user_id}`}>
                  {a.affiliate_active ? "Turn off" : "Approve"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GrowConfig() {
  const [cfg, setCfg] = useState(null);
  const [auto, setAuto] = useState(null);
  const [lb, setLb] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/grow-config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/invite-autopilot").then(({ data }) => setAuto(data)).catch(() => {});
    api.get("/admin/leaderboard-email").then(({ data }) => setLb(data)).catch(() => {});
  }, []);
  const saveLb = async (next) => {
    setLb(next);
    try { await api.put("/admin/leaderboard-email", { enabled: next.enabled, audience: next.audience }); toast.success("Leaderboard email updated"); }
    catch { toast.error("Could not update leaderboard"); }
  };
  const testLb = async () => {
    try { await api.post("/admin/leaderboard-email/test"); toast.success("Preview sent to your team inbox"); }
    catch { toast.error("Could not send preview"); }
  };
  const setRank = (i, k, v) => setCfg((c) => ({ ...c, ranks: c.ranks.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const addRank = () => setCfg((c) => ({ ...c, ranks: [...c.ranks, { key: "", name: "New Rank", min: 1, max: 99999, commission: 0 }] }));
  const delRank = (i) => setCfg((c) => ({ ...c, ranks: c.ranks.filter((_, j) => j !== i) }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/grow-config", {
        ranks: cfg.ranks.map((r) => ({ key: r.key, name: r.name, min: Number(r.min), max: Number(r.max), commission: Number(r.commission) })),
        max_bonus_cards: Number(cfg.max_bonus_cards), override_l1: Number(cfg.override_l1),
        override_l2: Number(cfg.override_l2), override_min_cards: Number(cfg.override_min_cards),
      });
      toast.success("Grow settings saved");
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  const saveAuto = async (next) => {
    setAuto(next);
    try { await api.put("/admin/invite-autopilot", { enabled: next.enabled, days: next.days, max_followups: next.max_followups }); toast.success("Invite autopilot updated"); }
    catch { toast.error("Could not update autopilot"); }
  };
  if (!cfg) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="grow-config">
      <h3 className="font-display font-bold text-sam-navy mb-1">Grow settings — ranks, milestone &amp; overrides</h3>
      <p className="text-[11px] text-sam-slate mb-3">These drive the rank badges and commission % shown across the app. Actual payouts are managed in Referly.</p>
      <div className="space-y-2">
        {cfg.ranks.map((r, i) => (
          <div key={i} className="flex flex-wrap items-end gap-2" data-testid={`rank-row-${i}`}>
            <div><div className="text-[10px] text-sam-slate">Name</div><Input value={r.name} onChange={(e) => setRank(i, "name", e.target.value)} className="h-8 w-36 text-xs" data-testid={`rank-name-${i}`} /></div>
            <div><div className="text-[10px] text-sam-slate">Min</div><Input type="number" value={r.min} onChange={(e) => setRank(i, "min", e.target.value)} className="h-8 w-16 text-xs" data-testid={`rank-min-${i}`} /></div>
            <div><div className="text-[10px] text-sam-slate">Max</div><Input type="number" value={r.max} onChange={(e) => setRank(i, "max", e.target.value)} className="h-8 w-20 text-xs" data-testid={`rank-max-${i}`} /></div>
            <div><div className="text-[10px] text-sam-slate">Commission %</div><Input type="number" step="0.5" value={r.commission} onChange={(e) => setRank(i, "commission", e.target.value)} className="h-8 w-24 text-xs" data-testid={`rank-commission-${i}`} /></div>
            <Button size="sm" variant="outline" className="h-8 text-sam-crimson" onClick={() => delRank(i)} data-testid={`rank-del-${i}`}>Remove</Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addRank} data-testid="rank-add">+ Add rank</Button>
      </div>
      <div className="flex flex-wrap items-end gap-3 mt-4">
        <div><div className="text-[10px] text-sam-slate">Max-bonus cards</div><Input type="number" value={cfg.max_bonus_cards} onChange={(e) => setCfg({ ...cfg, max_bonus_cards: e.target.value })} className="h-8 w-24 text-xs" data-testid="cfg-max-bonus" /></div>
        <div><div className="text-[10px] text-sam-slate">Override L1 %</div><Input type="number" step="0.5" value={cfg.override_l1} onChange={(e) => setCfg({ ...cfg, override_l1: e.target.value })} className="h-8 w-24 text-xs" data-testid="cfg-override-l1" /></div>
        <div><div className="text-[10px] text-sam-slate">Override L2 %</div><Input type="number" step="0.5" value={cfg.override_l2} onChange={(e) => setCfg({ ...cfg, override_l2: e.target.value })} className="h-8 w-24 text-xs" data-testid="cfg-override-l2" /></div>
        <div><div className="text-[10px] text-sam-slate">Override unlock cards</div><Input type="number" value={cfg.override_min_cards} onChange={(e) => setCfg({ ...cfg, override_min_cards: e.target.value })} className="h-8 w-28 text-xs" data-testid="cfg-override-min" /></div>
        <Button size="sm" className="h-8 bg-sam-crimson hover:bg-sam-crimson/90" onClick={save} disabled={busy} data-testid="grow-config-save">{busy ? "Saving…" : "Save grow settings"}</Button>
      </div>
      {auto && (
        <div className="mt-4 pt-4 border-t border-border/60" data-testid="autopilot-config">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm font-semibold text-sam-navy">Invite autopilot</div>
              <p className="text-[11px] text-sam-slate">Auto follow-up email/text to invitees who haven't joined. {auto.open_invites} open invite{auto.open_invites === 1 ? "" : "s"}.</p>
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-semibold" data-testid="autopilot-toggle">
              <input type="checkbox" className="accent-sam-crimson" checked={auto.enabled} onChange={(e) => saveAuto({ ...auto, enabled: e.target.checked })} />
              {auto.enabled ? "On" : "Off"}
            </label>
          </div>
          <div className="flex items-end gap-3 mt-2">
            <div><div className="text-[10px] text-sam-slate">Follow-up days (comma)</div><Input defaultValue={(auto.days || []).join(", ")} onBlur={(e) => saveAuto({ ...auto, days: e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter(Boolean) })} className="h-8 w-32 text-xs" data-testid="autopilot-days" /></div>
            <div><div className="text-[10px] text-sam-slate">Max follow-ups</div><Input type="number" defaultValue={auto.max_followups} onBlur={(e) => saveAuto({ ...auto, max_followups: parseInt(e.target.value, 10) || 2 })} className="h-8 w-24 text-xs" data-testid="autopilot-max" /></div>
          </div>
        </div>
      )}
      {lb && (
        <div className="mt-4 pt-4 border-t border-border/60" data-testid="leaderboard-config">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm font-semibold text-sam-navy">Monday leaderboard email</div>
              <p className="text-[11px] text-sam-slate">Celebrates the top card-sellers &amp; recruiters every Monday.</p>
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-semibold" data-testid="leaderboard-toggle">
              <input type="checkbox" className="accent-sam-crimson" checked={lb.enabled} onChange={(e) => saveLb({ ...lb, enabled: e.target.checked })} />
              {lb.enabled ? "On" : "Off"}
            </label>
          </div>
          <div className="flex items-end gap-3 mt-2 flex-wrap">
            <div>
              <div className="text-[10px] text-sam-slate">Send to</div>
              <select value={lb.audience} onChange={(e) => saveLb({ ...lb, audience: e.target.value })} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="leaderboard-audience">
                <option value="team">My team (notify inbox)</option>
                <option value="affiliates">All affiliates</option>
                <option value="both">Team + affiliates</option>
              </select>
            </div>
            <Button size="sm" variant="outline" className="h-8" onClick={testLb} data-testid="leaderboard-test">Send me a preview</Button>
          </div>
          {lb.preview && (
            <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs" data-testid="leaderboard-preview">
              <div>
                <div className="font-semibold text-sam-navy mb-1">Top card-sellers</div>
                {(lb.preview.sellers || []).length === 0 ? <div className="text-sam-slate">No entries yet</div> :
                  lb.preview.sellers.map((s, i) => <div key={i} className="flex justify-between"><span>{i + 1}. {s.name}</span><span className="text-sam-slate">{s.cards} cards</span></div>)}
              </div>
              <div>
                <div className="font-semibold text-sam-navy mb-1">Top recruiters</div>
                {(lb.preview.recruiters || []).length === 0 ? <div className="text-sam-slate">No entries yet</div> :
                  lb.preview.recruiters.map((s, i) => <div key={i} className="flex justify-between"><span>{i + 1}. {s.name}</span><span className="text-sam-slate">{s.recruits} recruits</span></div>)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TeamPanel() {
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [perms, setPerms] = useState([]);
  const [busy, setBusy] = useState(false);
  const AREAS = [["leads", "Leads & Directory"], ["sms", "SMS Inbox"], ["plans", "Plans & Sub-accounts"], ["credits", "Credit Budgets"], ["seo", "SEO & Blog"], ["outreach", "Outreach"]];
  const [acts, setActs] = useState(null);
  const [fPerson, setFPerson] = useState("");
  const [fAction, setFAction] = useState("");
  const [skip, setSkip] = useState(0);
  const load = () => api.get("/admin/team").then(({ data }) => setD(data)).catch(() => {});
  const loadActs = (sk = 0) => {
    const params = new URLSearchParams({ skip: String(sk), limit: "25" });
    if (fPerson) params.set("actor", fPerson);
    if (fAction) params.set("action", fAction);
    api.get(`/admin/activity?${params.toString()}`).then(({ data }) => {
      setActs((prev) => ({ ...data, items: sk > 0 ? [...(prev?.items || []), ...data.items] : data.items }));
      setSkip(sk);
    }).catch(() => setActs({ items: [], total: 0, has_more: false, actors: [], actions: [] }));
  };
  const resend = async (uid) => { try { await api.post(`/admin/team/${uid}/resend`); toast.success("Invite re-sent"); } catch { toast.error("Could not re-send"); } };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadActs(0); /* eslint-disable-next-line */ }, [fPerson, fAction]);
  const toggle = (a) => setPerms((p) => p.includes(a) ? p.filter((x) => x !== a) : [...p, a]);
  const invite = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    setBusy(true);
    try { await api.post("/admin/team/invite", { email: email.trim(), name: name.trim(), perms }); toast.success("Invite sent"); setEmail(""); setName(""); setPerms([]); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not invite"); } finally { setBusy(false); }
  };
  const savePerms = async (uid, p) => { try { await api.patch(`/admin/team/${uid}`, { perms: p }); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };
  const saveScope = async (uid, scope) => { try { await api.patch(`/admin/team/${uid}`, { sms_scope: scope }); toast.success("Inbox access updated"); load(); } catch { toast.error("Failed"); } };
  const saveReferly = async (uid, code) => { try { await api.patch(`/admin/team/${uid}`, { referly_code: code }); toast.success("Affiliate code saved"); load(); } catch { toast.error("Failed"); } };
  const savePhone = async (uid, p) => { try { await api.patch(`/admin/team/${uid}`, { alert_phone: p }); toast.success("Alert mobile saved"); } catch { toast.error("Failed"); } };
  const saveZips = async (uid, z) => { try { await api.patch(`/admin/team/${uid}`, { territory_zips: z }); toast.success("Area ZIPs saved"); load(); } catch { toast.error("Failed"); } };
  const saveRecruit = async (uid, link) => { try { await api.patch(`/admin/team/${uid}`, { referly_recruit_link: link }); toast.success("Recruitment link saved"); } catch { toast.error("Failed"); } };
  const remove = async (uid) => { try { await api.delete(`/admin/team/${uid}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); } };
  const prettyAction = (a) => (a || "").replace(/_/g, " ");
  if (!d) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;
  return (
    <div className="space-y-6" data-testid="team-panel">
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="font-display font-bold text-sam-navy mb-2">Invite a team member</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <Input placeholder="name" value={name} onChange={(e) => setName(e.target.value)} className="w-40" data-testid="team-invite-name" />
          <Input placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-64" data-testid="team-invite-email" />
          <Button onClick={invite} disabled={busy} data-testid="team-invite-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invite"}</Button>
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          {AREAS.map(([a, label]) => (
            <label key={a} className="inline-flex items-center gap-1.5" data-testid={`team-invite-perm-${a}`}>
              <input type="checkbox" className="accent-sam-crimson" checked={perms.includes(a)} onChange={() => toggle(a)} />{label}
            </label>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-sam-slate">They'll get an email link to set their own password. They can only access the areas you check.</p>
      </div>
      <ReferlySettings />
      <RepRanks />
      <AffiliatesSection />
      <GrowConfig />
      <div className="rounded-xl border border-border bg-card p-4" data-testid="activity-log">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display font-bold text-sam-navy">Recent activity {acts?.total ? <span className="text-xs font-normal text-sam-slate">({acts.total} total)</span> : null}</h3>
          <div className="flex gap-2">
            <select value={fPerson} onChange={(e) => setFPerson(e.target.value)} className="text-xs border border-border rounded-md px-2 py-1 bg-background" data-testid="activity-filter-person">
              <option value="">All people</option>
              {(acts?.actors || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={fAction} onChange={(e) => setFAction(e.target.value)} className="text-xs border border-border rounded-md px-2 py-1 bg-background" data-testid="activity-filter-action">
              <option value="">All actions</option>
              {(acts?.actions || []).map((a) => <option key={a} value={a}>{prettyAction(a)}</option>)}
            </select>
          </div>
        </div>
        {!acts ? <Loader2 className="h-5 w-5 animate-spin text-sam-slate" /> : acts.items.length === 0 ? <p className="text-sm text-sam-slate">No activity yet.</p> : (
          <div className="space-y-1.5 max-h-96 overflow-y-auto text-xs">
            {acts.items.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 border-b border-border/50 pb-1" data-testid={`activity-${a.id}`}>
                <span className="text-sam-navy"><b>{a.name || a.email}</b> {prettyAction(a.action)} {a.detail && <span className="text-sam-slate">({a.detail})</span>}</span>
                <span className="text-sam-slate shrink-0">{new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
            {acts.has_more && <Button size="sm" variant="outline" className="mt-2" onClick={() => loadActs(skip + 25)} data-testid="activity-load-more">Load more</Button>}
          </div>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="font-display font-bold text-sam-navy mb-3">Team members</h3>
        {d.members.length === 0 ? <p className="text-sm text-sam-slate">No team members yet.</p> : (
          <div className="space-y-3">
            {d.members.map((m) => (
              <div key={m.user_id} className="border-b border-border/60 pb-3" data-testid={`team-member-${m.user_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div><span className="font-semibold text-sam-navy">{m.name}</span> <span className="text-sm text-sam-slate">{m.email}</span>{" "}
                    <Badge className={m.status === "invited" ? "bg-sam-amber" : "bg-sam-green"}>{m.status}</Badge></div>
                  <div className="flex items-center gap-2">
                    {m.status === "invited" && <Button size="sm" variant="outline" onClick={() => resend(m.user_id)} data-testid={`team-resend-${m.user_id}`}>Resend</Button>}
                    <Button size="sm" variant="outline" className="text-sam-crimson" onClick={() => remove(m.user_id)} data-testid={`team-remove-${m.user_id}`}>Remove</Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs mt-2">
                  {AREAS.map(([a, label]) => (
                    <label key={a} className="inline-flex items-center gap-1" data-testid={`team-perm-${m.user_id}-${a}`}>
                      <input type="checkbox" className="accent-sam-crimson" checked={(m.perms || []).includes(a)}
                        onChange={() => savePerms(m.user_id, (m.perms || []).includes(a) ? m.perms.filter((x) => x !== a) : [...(m.perms || []), a])} />{label}
                    </label>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`team-referly-${m.user_id}`}>
                  <span className="text-sam-slate">Referly affiliate code:</span>
                  <Input defaultValue={m.referly_code || ""} onBlur={(e) => saveReferly(m.user_id, e.target.value)} placeholder="e.g. JANE20" className="h-7 w-44 text-xs" data-testid={`team-referly-input-${m.user_id}`} />
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`team-zips-${m.user_id}`}>
                  <span className="text-sam-slate">Area ZIPs (auto-assign new runs):</span>
                  <Input defaultValue={(m.territory_zips || []).join(", ")} onBlur={(e) => saveZips(m.user_id, e.target.value)} placeholder="e.g. 65708, 65804" className="h-7 w-72 text-xs" data-testid={`team-zips-input-${m.user_id}`} />
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`team-phone-${m.user_id}`}>
                  <span className="text-sam-slate">Mobile for near-ready card texts:</span>
                  <Input type="tel" defaultValue={m.alert_phone || ""} onBlur={(e) => e.target.value !== (m.alert_phone || "") && savePhone(m.user_id, e.target.value)} placeholder="e.g. 417-555-0123" className="h-7 w-44 text-xs" data-testid={`team-phone-input-${m.user_id}`} />
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`team-recruit-${m.user_id}`}>
                  <span className="text-sam-slate">Referly recruitment link:</span>
                  <Input defaultValue={m.referly_recruit_link || ""} onBlur={(e) => saveRecruit(m.user_id, e.target.value)} placeholder="paste from Referly → My Team → Invite Affiliate" className="h-7 w-72 text-xs" data-testid={`team-recruit-input-${m.user_id}`} />
                </div>
                {(m.perms || []).includes("sms") && (d.subaccounts || []).length > 0 && (
                  <div className="mt-2 rounded-lg bg-slate-50 border border-border/60 p-2.5" data-testid={`team-sms-scope-${m.user_id}`}>
                    <p className="text-[11px] font-semibold text-sam-navy mb-1.5">Inbox access <span className="font-normal text-sam-slate">— leave all unchecked to see the agency inbox (default)</span></p>
                    <div className="flex flex-wrap gap-2.5 text-xs">
                      {d.subaccounts.map((s) => {
                        const on = (m.sms_scope || []).includes(s.workspace_id);
                        return (
                          <label key={s.workspace_id} className="inline-flex items-center gap-1" data-testid={`team-scope-${m.user_id}-${s.workspace_id}`}>
                            <input type="checkbox" className="accent-sam-crimson" checked={on}
                              onChange={() => saveScope(m.user_id, on ? (m.sms_scope || []).filter((x) => x !== s.workspace_id) : [...(m.sms_scope || []), s.workspace_id])} />{s.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfitByTier({ byTier }) {
  const TIERS = [
    { key: "listing", label: "Listing", price: 29 },
    { key: "growth", label: "Growth", price: 97 },
    { key: "pro", label: "Pro", price: 297 },
    { key: "max", label: "Max", price: 497 },
  ];
  const rows = TIERS.map((t) => ({ ...t, ...(byTier?.[t.key] || { clients: 0, margin: 0, revenue: 0 }) }));
  const maxMargin = Math.max(1, ...rows.map((r) => Math.abs(r.margin || 0)));
  const best = rows.reduce((a, b) => ((b.margin || 0) > (a.margin || 0) ? b : a), rows[0]);
  const anyData = rows.some((r) => r.clients > 0);
  if (!anyData) return null;
  return (
    <div className="mb-4 rounded-lg border border-border bg-background p-3" data-testid="profit-by-tier">
      <div className="flex items-center gap-1.5 mb-2">
        <TrendingUp className="h-4 w-4 text-sam-green" />
        <span className="text-sm font-semibold text-sam-navy">Profit by plan tier (this month)</span>
        {best.margin > 0 && <span className="text-xs text-sam-green font-semibold ml-1">Most profitable: {best.label}</span>}
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => {
          const w = Math.round((Math.abs(r.margin || 0) / maxMargin) * 100);
          const neg = (r.margin || 0) < 0;
          const isBest = r.key === best.key && r.margin > 0;
          return (
            <div key={r.key} className="flex items-center gap-2 text-xs" data-testid={`profit-tier-${r.key}`}>
              <span className="w-16 shrink-0 text-sam-slate">{r.label}</span>
              <div className="flex-1 h-4 rounded bg-slate-100 overflow-hidden">
                <div className={`h-full ${neg ? "bg-sam-crimson" : isBest ? "bg-sam-green" : "bg-sam-navy/60"}`} style={{ width: `${Math.max(w, r.clients ? 4 : 0)}%` }} />
              </div>
              <span className={`w-28 shrink-0 text-right font-semibold ${neg ? "text-sam-crimson" : "text-sam-navy"}`}>
                ${Math.round(r.margin || 0)} <span className="font-normal text-sam-slate">({r.clients})</span>
              </span>
            </div>
          );
        })}
      </div>
      <p className="mt-1.5 text-[10px] text-sam-slate">Bar = total monthly profit per tier (revenue − Stripe fees − credits used × cost). ( ) = clients on that tier.</p>
    </div>
  );
}

function PoolPlanner({ d }) {
  const [n, setN] = useState(1);
  const [tier, setTier] = useState("growth");
  const PRICE = { listing: 29, growth: 97, pro: 297, max: 497 };
  const allowance = (d.tiers && d.tiers[tier]) || 0;
  const price = PRICE[tier] || 0;
  const cpc = d.cost_per_credit || 0.24;
  const count = Math.max(0, Number(n) || 0);
  const addlCommitted = count * allowance;
  const newCommitted = d.committed + addlCommitted;
  const headroom = d.pool - newCommitted;
  const fee = price * 0.029 + 0.3;
  const addlRevenue = count * price;
  const addlMargin = count * (price - fee - allowance * cpc);
  return (
    <div className="mb-4 rounded-lg border border-border bg-background p-3" data-testid="pool-planner">
      <div className="flex items-center gap-1.5 mb-2">
        <Target className="h-4 w-4 text-sam-navy" />
        <span className="text-sm font-semibold text-sam-navy">Pool planner — model adding clients</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Add how many</Label>
          <Input type="number" min="0" value={n} onChange={(e) => setN(e.target.value)} className="h-8 w-20" data-testid="planner-count" />
        </div>
        <div>
          <Label className="text-xs">On tier</Label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="h-8 rounded border border-border px-2 text-sm block" data-testid="planner-tier">
            <option value="listing">Listing</option>
            <option value="growth">Growth</option>
            <option value="pro">Pro</option>
            <option value="max">Max</option>
          </select>
        </div>
        <div className="flex-1 min-w-[260px] grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div className="rounded border border-border p-2"><div className="text-sam-slate">New committed</div><div className={`font-bold ${newCommitted > d.pool ? "text-sam-crimson" : "text-sam-navy"}`} data-testid="planner-committed">{newCommitted}/{d.pool}</div></div>
          <div className="rounded border border-border p-2"><div className="text-sam-slate">Pool headroom</div><div className={`font-bold ${headroom < 0 ? "text-sam-crimson" : "text-sam-green"}`} data-testid="planner-headroom">{headroom}</div></div>
          <div className="rounded border border-border p-2"><div className="text-sam-slate">Added revenue</div><div className="font-bold text-sam-navy" data-testid="planner-revenue">${Math.round(addlRevenue)}/mo</div></div>
          <div className="rounded border border-border p-2"><div className="text-sam-slate">Added profit*</div><div className={`font-bold ${addlMargin < 0 ? "text-sam-crimson" : "text-sam-green"}`} data-testid="planner-margin">${Math.round(addlMargin)}/mo</div></div>
        </div>
      </div>
      {headroom < 0 && <p className="mt-1.5 text-xs font-semibold text-sam-crimson" data-testid="planner-warning">This would over-commit the {d.pool}-credit pool by {Math.abs(headroom)} credits.</p>}
      <p className="mt-1.5 text-[10px] text-sam-slate">*Worst-case profit assuming each new client uses their full allowance (revenue − Stripe fee − allowance × cost/credit).</p>
    </div>
  );
}

function CreditBudgetsPanel() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState({ listing: "", growth: "", pro: "", max: "" });
  const [pool, setPool] = useState("");
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ovInput, setOvInput] = useState({});
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/credit-budgets").then(({ data }) => {
      setD(data);
      setTiers({ listing: data.tiers.listing, growth: data.tiers.growth, pro: data.tiers.pro, max: data.tiers.max });
      setPool(data.pool);
      setCost(data.cost_per_credit);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/credit-budgets", {
        tiers: { listing: Number(tiers.listing), growth: Number(tiers.growth), pro: Number(tiers.pro), max: Number(tiers.max) },
        pool: Number(pool),
        cost_per_credit: Number(cost),
      });
      toast.success("Budgets saved"); load();
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const saveOverride = async (sid) => {
    const v = ovInput[sid];
    try {
      await api.patch(`/admin/credit-budgets/${sid}`, { override: v === "" || v == null ? null : Number(v) });
      toast.success("Override updated"); load();
    } catch { toast.error("Could not update override"); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/credit-budgets/sync"); toast.success(`Synced ${data.synced} of ${data.count}`); load(); }
    catch { toast.error("Sync failed"); } finally { setSyncing(false); }
  };

  if (loading) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;
  if (!d) return null;
  const statusBadge = (s) => s === "paused"
    ? <Badge className="bg-sam-crimson">paused</Badge>
    : s === "warned" ? <Badge className="bg-sam-amber">80%</Badge> : <Badge className="bg-sam-green">ok</Badge>;

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="credit-budgets-panel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-sam-navy inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-sam-amber" />Credit Budgets</h3>
          <p className="text-[11px] text-sam-slate">Monthly marketing-credit allowance per client sub-account, drawn from the agency pool.</p>
        </div>
        <Button size="sm" variant="outline" onClick={sync} disabled={syncing} data-testid="credits-sync-btn">
          {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Sync usage now
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        {["listing", "growth", "pro", "max"].map((k) => (
          <div key={k}>
            <Label className="text-xs capitalize">{k} / mo</Label>
            <Input type="number" value={tiers[k]} onChange={(e) => setTiers({ ...tiers, [k]: e.target.value })} data-testid={`credit-tier-${k}`} />
          </div>
        ))}
        <div>
          <Label className="text-xs">Agency pool / mo</Label>
          <Input type="number" value={pool} onChange={(e) => setPool(e.target.value)} data-testid="credit-pool" />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        <div>
          <Label className="text-xs">Agency cost / credit ($)</Label>
          <Input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="credit-cost-per-credit" />
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <Button size="sm" onClick={saveSettings} disabled={saving} data-testid="credit-save-btn">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Save budgets</Button>
        <span className="text-sm" data-testid="credit-pool-summary">
          Committed <b className={d.over_committed ? "text-sam-crimson" : "text-sam-navy"}>{d.committed}</b> / {d.pool} pool · Used <b className="text-sam-navy">{Math.round(d.total_used)}</b> · Est. margin <b className={d.total_margin < 0 ? "text-sam-crimson" : "text-sam-green"}>${Math.round(d.total_margin)}</b>/mo
        </span>
        {d.nudge_funnel && d.nudge_funnel.sent > 0 && <span className="text-xs text-sam-slate" data-testid="nudge-funnel">· Nudges {d.nudge_funnel.sent} sent → {d.nudge_funnel.opened} opened → {d.nudge_funnel.upgraded} upgraded ({Math.round((d.nudge_funnel.upgraded / d.nudge_funnel.sent) * 100)}% conversion)</span>}
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-sam-crimson" data-testid="credit-overcommit-warning">
            <AlertTriangle className="h-3.5 w-3.5" />Allowances exceed the {d.pool}-credit pool
          </span>
        )}
      </div>

      {d.accounts.length === 0 ? (
        <p className="text-sm text-sam-slate">No provisioned sub-accounts yet.</p>
      ) : (
        <>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="credit-margin-dashboard">
          <div className="rounded-lg border border-border bg-background p-3"><div className="text-[10px] uppercase tracking-wider text-sam-slate">Clients</div><div className="text-xl font-display font-bold text-sam-navy">{d.client_count}</div></div>
          <div className="rounded-lg border border-border bg-background p-3"><div className="text-[10px] uppercase tracking-wider text-sam-slate">Revenue / mo</div><div className="text-xl font-display font-bold text-sam-navy">${Math.round(d.total_revenue)}</div></div>
          <div className="rounded-lg border border-border bg-background p-3"><div className="text-[10px] uppercase tracking-wider text-sam-slate">Credit cost / mo</div><div className="text-xl font-display font-bold text-sam-navy">${Math.round(d.total_credit_cost)}</div><div className="text-[10px] text-sam-slate">+ ${Math.round(d.total_fees)} fees</div></div>
          <div className="rounded-lg border border-border bg-background p-3" data-testid="credit-total-margin"><div className="text-[10px] uppercase tracking-wider text-sam-slate">Est. profit / mo</div><div className={`text-xl font-display font-bold ${d.total_margin < 0 ? "text-sam-crimson" : "text-sam-green"}`}>${Math.round(d.total_margin)}</div></div>
          <div className={`rounded-lg border p-3 ${d.over_committed ? "border-sam-crimson/40 bg-sam-crimson/5" : "border-border bg-background"}`}><div className="text-[10px] uppercase tracking-wider text-sam-slate">Pool committed</div><div className={`text-xl font-display font-bold ${d.over_committed ? "text-sam-crimson" : "text-sam-navy"}`}>{d.committed}/{d.pool}</div><div className="text-[10px] text-sam-slate">{d.used ? "" : ""}used {Math.round(d.total_used)}</div></div>
        </div>
        <ProfitByTier byTier={d.by_tier} />
        <PoolPlanner d={d} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-sam-slate border-b border-border">
              <th className="py-2 pr-3">Business</th><th className="py-2 pr-3">Tier</th><th className="py-2 pr-3">Usage</th>
              <th className="py-2 pr-3">Allowance</th><th className="py-2 pr-3">Override</th><th className="py-2 pr-3">Est. cost</th><th className="py-2 pr-3">Margin</th><th className="py-2 pr-3">Status</th>
            </tr></thead>
            <tbody>
              {d.accounts.map((a) => {
                const pct = Math.min(100, Math.round((a.pct || 0) * 100));
                const bar = pct >= 100 ? "bg-sam-crimson" : pct >= 80 ? "bg-sam-amber" : "bg-sam-green";
                return (
                  <tr key={a.signup_id} className="border-b border-border/60" data-testid={`credit-row-${a.signup_id}`}>
                    <td className="py-2.5 pr-3"><div className="font-semibold text-sam-navy">{a.business_name}</div><div className="text-xs text-sam-slate">{a.email}</div>
                      {a.nudge_sent && <span className="text-[10px] font-semibold" data-testid={`nudge-${a.signup_id}`}>{a.nudge_upgraded ? <span className="text-sam-green">↑ upgraded after nudge</span> : a.nudge_opened ? <span className="text-sam-navy">nudge opened ({a.nudge_clicks})</span> : <span className="text-sam-slate">nudge sent</span>}</span>}
                    </td>
                    <td className="py-2.5 pr-3"><Badge className="bg-sam-navy capitalize">{a.plan}</Badge></td>
                    <td className="py-2.5 pr-3 w-40">
                      <div className="text-xs text-sam-navy font-semibold mb-1" data-testid={`credit-use-${a.signup_id}`}>{Math.round(a.total_credits)} / {a.allowance} ({pct}%)</div>
                      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${pct}%` }} /></div>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-sam-slate">{a.base}{a.topup ? ` +${a.topup}` : ""}</td>
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-1.5">
                        <Input type="number" className="h-8 w-20" placeholder={String(a.base)}
                          defaultValue={a.override == null ? "" : a.override}
                          onChange={(e) => setOvInput({ ...ovInput, [a.signup_id]: e.target.value })}
                          data-testid={`credit-override-${a.signup_id}`} />
                        <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => saveOverride(a.signup_id)} data-testid={`credit-override-save-${a.signup_id}`}>Set</Button>
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-sam-slate" data-testid={`credit-cost-${a.signup_id}`}>${a.credit_cost}</td>
                    <td className="py-2.5 pr-3 text-xs font-semibold" data-testid={`credit-margin-${a.signup_id}`}><span className={a.margin < 0 ? "text-sam-crimson" : "text-sam-green"}>${a.margin}</span></td>
                    <td className="py-2.5 pr-3">{statusBadge(a.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

function SeoBlogPanel() {
  const [posts, setPosts] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/blog/posts").then(({ data }) => setPosts(data.posts || [])).catch(() => {}),
      api.get("/admin/seo/overrides").then(({ data }) => setOverrides(data.overrides || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try { await api.patch(`/admin/blog/posts/${p.id}`, { status: next }); toast.success(next === "published" ? "Published" : "Unpublished"); load(); }
    catch { toast.error("Could not update"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/blog/posts/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  const removeOverride = async (o) => {
    try { await api.delete(`/admin/seo/overrides/${o.id}`); toast.success("Override removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;

  return (
    <div className="space-y-6" data-testid="seo-blog-panel">
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="font-display font-bold text-sam-navy mb-1">Blog posts</h3>
        <p className="text-[11px] text-sam-slate mb-3">Articles published by the Nuwtonic / n8n integration. Edit links open the live post.</p>
        {posts.length === 0 ? (
          <p className="text-sm text-sam-slate">No posts yet. They appear here once your n8n workflow publishes to the Nuwtonic articles endpoint.</p>
        ) : (
          <ul className="divide-y divide-border">
            {posts.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`blog-row-${p.slug}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sam-navy truncate">{p.title}</span>
                    <Badge className={p.status === "published" ? "bg-sam-green" : "bg-slate-400"}>{p.status}</Badge>
                  </div>
                  <div className="text-xs text-sam-slate truncate">/blog/{p.slug}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex"><Button size="sm" variant="outline" data-testid={`blog-edit-${p.slug}`}><ExternalLink className="h-3.5 w-3.5 mr-1" />View</Button></a>
                  <Button size="sm" variant="outline" onClick={() => toggleStatus(p)} data-testid={`blog-toggle-${p.slug}`}>{p.status === "published" ? "Unpublish" : "Publish"}</Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-sam-crimson" onClick={() => del(p)} data-testid={`blog-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="font-display font-bold text-sam-navy mb-1">Active SEO overrides</h3>
        <p className="text-[11px] text-sam-slate mb-3">Head-tag and content fixes applied at render time per page path.</p>
        {overrides.length === 0 ? (
          <p className="text-sm text-sam-slate">No SEO overrides active.</p>
        ) : (
          <ul className="divide-y divide-border">
            {overrides.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`seo-override-${o.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className={o.supported ? "bg-sam-navy" : "bg-sam-amber"}>{o.type}</Badge>
                    <span className="font-mono text-xs text-sam-slate truncate">{o.path}{o.selector ? ` · ${o.selector}` : ""}</span>
                  </div>
                  <div className="text-xs text-sam-slate truncate mt-0.5">{o.value}</div>
                </div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-sam-crimson shrink-0" onClick={() => removeOverride(o)} data-testid={`seo-remove-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ShareLog() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [hold, setHold] = useState(48);
  const [fb, setFb] = useState({ facebook_page_id: "", facebook_graph_version: "v21.0", facebook_connected: false });
  const [fbToken, setFbToken] = useState("");
  const [savingFb, setSavingFb] = useState(false);
  const load = () => api.get("/admin/share-log").then(({ data }) => setRows(data.entries || [])).catch(() => setRows([]));
  const loadStats = () => api.get("/admin/share-log/analytics").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => {
    load(); loadStats();
    api.get("/admin/reopen-hold").then(({ data }) => setHold(data.hours || 48)).catch(() => {});
    api.get("/admin/social-settings").then(({ data }) => setFb(data)).catch(() => {});
  }, []);
  const patch = async (id, upd) => { try { await api.patch(`/admin/share-log/${id}`, upd); load(); loadStats(); } catch { toast.error("Could not update"); } };
  const saveHold = async (h) => {
    setHold(h);
    try { await api.post("/admin/reopen-hold", { hours: h }); toast.success(`Reopened spots now hold for ${h}h`); }
    catch { toast.error("Could not save hold window"); }
  };
  const saveFb = async () => {
    setSavingFb(true);
    try {
      const body = { facebook_page_id: fb.facebook_page_id || "" };
      if (fbToken.trim()) body.facebook_page_token = fbToken.trim();
      const { data } = await api.post("/admin/social-settings", body);
      setFb((f) => ({ ...f, facebook_connected: data.facebook_connected }));
      setFbToken("");
      toast.success(data.facebook_connected ? "Facebook Page connected — reopened spots will auto-post" : "Saved");
    } catch { toast.error("Could not save Facebook settings"); }
    finally { setSavingFb(false); }
  };
  const disconnectFb = async () => {
    try {
      await api.post("/admin/social-settings", { facebook_clear: true });
      setFb((f) => ({ ...f, facebook_connected: false, facebook_page_id: "" }));
      setFbToken("");
      toast.success("Facebook auto-posting turned off");
    } catch { toast.error("Could not disconnect Facebook"); }
  };
  const [aiBusy, setAiBusy] = useState(null);
  const aiPost = async (r) => {
    setAiBusy(r.id);
    try {
      const { data } = await api.post(`/admin/share-log/${r.id}/ai-post`, {});
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, social_post: data.social_post } : x)));
      navigator.clipboard?.writeText(data.social_post); toast.success("Claude wrote a fresh post — copied");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI writer unavailable"); }
    setAiBusy(null);
  };
  const copyPost = (r) => {
    const text = r.social_post ? `${r.social_post}` : `A spot just opened in ZIP ${r.zip || ""} — claim it: ${r.link}`;
    navigator.clipboard?.writeText(text); toast.success("Social post copied — paste into Facebook/Instagram");
  };
  if (!rows) return null;
  const Stat = ({ label, value, Icon, color }) => (
    <div className="rounded-lg border border-border bg-background p-3 text-center" data-testid={`turnover-${label.toLowerCase().replace(/ /g, "-")}`}>
      <Icon className={`h-4 w-4 mx-auto ${color}`} />
      <div className="font-display font-black text-xl text-sam-navy mt-1">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-sam-slate">{label}</div>
    </div>
  );
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4" data-testid="share-log">
      <div>
        <h3 className="font-display font-bold text-sam-navy mb-1 flex items-center gap-2"><Repeat className="h-4 w-4 text-sam-crimson" />Spot turnover</h3>
        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2" data-testid="turnover-stats">
            <Stat label="Reopened" value={stats.reopened} Icon={Share2} color="text-sam-crimson" />
            <Stat label="Refilled" value={stats.refilled} Icon={Check} color="text-sam-green" />
            <Stat label="Still open" value={stats.open} Icon={AlertTriangle} color="text-sam-amber" />
            <Stat label="Refill rate" value={`${stats.refill_rate}%`} Icon={BarChart3} color="text-sam-navy" />
            <Stat label="Avg turnaround" value={stats.avg_turnaround_hours != null ? `${stats.avg_turnaround_hours}h` : "—"} Icon={Clock} color="text-sam-slate" />
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-border p-3" data-testid="reopen-hold-setting">
          <div className="flex items-center gap-2 text-xs font-semibold text-sam-navy mb-2"><Clock className="h-3.5 w-3.5" />Claim window for reopened spots</div>
          <div className="flex gap-2">
            {[24, 48, 72].map((h) => (
              <Button key={h} size="sm" variant={hold === h ? "default" : "outline"} className={`h-8 text-xs flex-1 ${hold === h ? "bg-sam-crimson hover:bg-sam-crimson/90" : ""}`} onClick={() => saveHold(h)} data-testid={`hold-${h}`}>{h}h</Button>
            ))}
          </div>
          <p className="text-[10px] text-sam-slate mt-1.5">Reopened spots hold this long before the urgency banner auto-recycles.</p>
        </div>

        <div className="rounded-lg border border-border p-3" data-testid="facebook-settings">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="flex items-center gap-2 text-xs font-semibold text-sam-navy"><Facebook className="h-3.5 w-3.5 text-[#1877F2]" />Auto-post to Facebook Page</span>
            {fb.facebook_connected && <Badge className="bg-sam-green text-[10px]">Connected</Badge>}
          </div>
          <Input className="h-8 text-xs mb-1.5" placeholder="Facebook Page ID" value={fb.facebook_page_id || ""} onChange={(e) => setFb({ ...fb, facebook_page_id: e.target.value })} data-testid="fb-page-id" />
          <Input className="h-8 text-xs mb-1.5" type="password" placeholder={fb.facebook_connected ? "Page token saved — paste to replace" : "Page access token"} value={fbToken} onChange={(e) => setFbToken(e.target.value)} data-testid="fb-page-token" />
          <Button size="sm" className="h-8 text-xs w-full bg-sam-navy" onClick={saveFb} disabled={savingFb} data-testid="fb-save">{savingFb ? "Saving…" : "Save Facebook settings"}</Button>
          {fb.facebook_connected && <Button size="sm" variant="ghost" className="h-7 text-xs w-full text-sam-crimson mt-1" onClick={disconnectFb} data-testid="fb-disconnect">Disconnect / turn off auto-post</Button>}
          <p className="text-[10px] text-sam-slate mt-1.5">When connected, reopened spots auto-post the "one spot left" offer to your Page. Leave blank to just copy/paste manually.</p>
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-sam-navy mb-1">Shared spot follow-ups</h3>
        <p className="text-[11px] text-sam-slate mb-3">Every open/reopened spot link you generate is logged here. Add who you sent it to and tick it off when they're in.</p>
        {rows.length === 0 ? <p className="text-sm text-sam-slate">No shared spots yet.</p> : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className={`rounded-lg border p-2.5 text-xs ${r.followed_up ? "opacity-60 border-border" : "border-sam-crimson/30"}`} data-testid={`sharelog-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold text-sam-navy">{r.route_code || "—"} · ZIP {r.zip || "—"} <span className="font-normal text-sam-slate">{r.type === "reopen" ? "· reopened" : "· shared"}</span></span>
                  <span className="text-sam-slate">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                </div>
                {r.note && <div className="text-sam-slate mt-0.5">{r.note}</div>}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <Input className="h-7 text-xs w-48" placeholder="Sent to (name / phone / email)" defaultValue={r.sent_to} onBlur={(e) => e.target.value !== (r.sent_to || "") && patch(r.id, { sent_to: e.target.value })} data-testid={`sharelog-sentto-${r.id}`} />
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { navigator.clipboard?.writeText(r.link); toast.success("Link copied"); }} data-testid={`sharelog-copy-${r.id}`}>Copy link</Button>
                  {(r.type === "reopen" || r.social_post) && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyPost(r)} data-testid={`sharelog-copypost-${r.id}`}><Share2 className="h-3 w-3 mr-1" />Copy post</Button>}
                  {(r.type === "reopen" || r.social_post) && <Button size="sm" variant="outline" className="h-7 text-xs" disabled={aiBusy === r.id} onClick={() => aiPost(r)} data-testid={`sharelog-aipost-${r.id}`}>{aiBusy === r.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}Rewrite with AI</Button>}
                  <Button size="sm" variant={r.followed_up ? "outline" : "default"} className={`h-7 text-xs ${r.followed_up ? "" : "bg-sam-green hover:bg-sam-green/90"}`} onClick={() => patch(r.id, { followed_up: !r.followed_up })} data-testid={`sharelog-done-${r.id}`}>{r.followed_up ? "Reopen" : "Mark done"}</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RunRow({ run, refCode }) {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [zp, setZp] = useState(null);
  const refresh = async () => { const { data } = await api.get(`/postcard-runs/${run.id}`); setSlots(data.slots); };
  const toggle = async () => {
    if (!open) {
      await refresh();
      if ((run.central_zip || "").trim()) api.get(`/admin/postcard-runs/${run.id}/zip-page`).then(({ data }) => setZp(data)).catch(() => {});
    }
    setOpen(!open);
  };
  const download = async (fmt) => {
    try {
      const { data } = await api.get(`/postcard-runs/${run.id}/export.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${run.route_code}-postcard.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const saveZp = async () => {
    try { await api.patch(`/admin/postcard-runs/${run.id}/zip-page`, { headline: zp.headline, custom_note: zp.custom_note }); toast.success("Mailer page updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update mailer page"); }
  };
  const mailerLink = () => {
    const q = new URLSearchParams({ zip: run.central_zip || "", city: run.city || "", state: run.state_abbr || "", ...(refCode ? { ref: refCode } : {}) });
    return `${window.location.origin}/mailer?${q.toString()}`;
  };
  const shareSpot = async () => {
    const link = mailerLink();
    try { await navigator.clipboard.writeText(link); toast.success("Buyer link copied — paste into a text or email"); }
    catch { toast.error("Copy failed — long-press the link to copy"); }
    api.post("/admin/share-log", { run_id: run.id, zip: run.central_zip || "", route_code: run.route_code, link, note: "Open spot shared" }).catch(() => {});
  };
  const reopen = async (sid) => {
    try {
      const { data } = await api.post(`/admin/postcard-slots/${sid}/reopen`);
      await refresh();
      try { await navigator.clipboard.writeText(data.share_url || mailerLink()); } catch { /* noop */ }
      const fbMsg = data?.facebook?.ok ? " · posted to Facebook" : "";
      toast.success(`Spot reopened — buyer link copied (holds ${data.hold_hours || 48}h)${fbMsg}`);
    } catch { toast.error("Could not reopen the spot"); }
  };
  const soldOut = run.available_count === 0;
  const totalSlots = (run.sold_count || 0) + (run.available_count || 0);
  const fillPct = totalSlots ? Math.round((run.sold_count / totalSlots) * 100) : 0;
  const fillColor = fillPct >= 100 ? "bg-sam-green" : fillPct >= 67 ? "bg-sam-crimson" : fillPct >= 34 ? "bg-sam-amber" : "bg-sam-slate";
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-display font-bold text-sam-navy flex items-center gap-2"><Mail className="h-4 w-4 text-sam-crimson" />{run.route_code} · {run.city}, {run.state_abbr}</div>
          <div className="text-sm text-sam-slate">{run.sold_count} sold · {run.available_count} open · {run.mailer_count.toLocaleString()} homes {soldOut && <span className="ml-1 font-bold text-sam-green">· CARD FULL ✓</span>}</div>
          <div className="mt-1.5 flex items-center gap-2" data-testid={`run-heat-${run.route_code}`}>
            <div className="h-1.5 w-32 rounded-full bg-sam-navy/10 overflow-hidden"><div className={`h-full ${fillColor}`} style={{ width: `${fillPct}%` }}></div></div>
            <span className="text-[11px] font-semibold text-sam-slate">{fillPct}% full{!soldOut && fillPct >= 67 ? " · push!" : ""}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" asChild><Link to={`/postcards/${run.id}`}>Preview</Link></Button>
          <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid={`export-pdf-${run.route_code}`}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={() => download("csv")} data-testid={`export-csv-${run.route_code}`}><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
          <Button size="sm" variant="ghost" onClick={toggle} data-testid={`manage-run-${run.route_code}`}>{open ? "Hide" : "Manage slots"}</Button>
        </div>
      </div>
      {open && (
        <div className="mt-3">
          {(run.central_zip || "").trim() && zp && (
            <div className="mb-3 rounded-lg border border-border p-3" data-testid={`run-zippage-${run.route_code}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-sam-navy">Mailer page {zp.url && <a href={zp.url} target="_blank" rel="noreferrer" className="font-mono text-sam-slate hover:text-sam-crimson">{zp.url}</a>}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={saveZp} data-testid={`save-zippage-${run.route_code}`}>Save page</Button>
              </div>
              <Input className="h-8 text-xs mb-2" placeholder="Headline (optional)" value={zp.headline} onChange={(e) => setZp({ ...zp, headline: e.target.value })} data-testid={`zippage-headline-${run.route_code}`} />
              <Textarea rows={2} className="text-xs" placeholder="Custom note (optional)" value={zp.custom_note} onChange={(e) => setZp({ ...zp, custom_note: e.target.value })} data-testid={`zippage-note-${run.route_code}`} />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            {slots.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border p-2 text-sm">
                <span className="font-mono text-xs">{s.side}·{s.slot_type} · {money(s.price)}{s.business_name ? ` · ${s.business_name}` : ""}</span>
                <div className="flex items-center gap-1.5">
                  <Badge className={s.status === "sold" ? "bg-sam-navy" : s.status === "reserved" ? "bg-sam-amber" : "bg-sam-green"}>{s.status}</Badge>
                  {s.status === "available"
                    ? ((run.central_zip || "").trim() ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={shareSpot} data-testid={`share-spot-${s.id}`}><Copy className="h-3 w-3 mr-1" />Share</Button> : null)
                    : <Button size="sm" variant="outline" className="h-7 text-xs text-sam-crimson" onClick={() => reopen(s.id)} data-testid={`reopen-spot-${s.id}`}>Reopen</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
